import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from "react";
import {
  LayoutDashboard, ShoppingCart, Package, Users, Wallet, Layers,
  Sun, Moon, Globe, LogOut, Plus, Search, Printer, Truck, DollarSign,
  X, CheckCircle2, Send, Trash2, Pencil, Filter, FileDown, ChevronRight, ChevronDown, ChevronLeft,
  AlertTriangle, TrendingUp, Boxes, Receipt, Settings, MessageCircle,
  Sparkles, Grid, Ruler, FileText, Factory, Upload, Bell, CreditCard, ClipboardList, RefreshCw,
  Home, ChevronsLeft, ChevronsRight, Mail, Share2, Eye, EyeOff, Check, CalendarDays, Camera, FileSignature, Target, MapPin, Tag, Palette, AlertCircle, Download,
  Brain, Zap, Cpu, Info, Building2, History, Phone, Shield, BarChart2, Bot, Radio, Copy, Barcode, PlusCircle, ShieldAlert, ExternalLink, Loader2, Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { AuthCtx, UICtx, StockSettingsCtx } from "../appContexts";
import { fmt, fmtDate, fmtDT, ono, MONEY, convertPrice } from "../appUtils";
import { translations, CURRENCY_SYMBOL, STATUS_LABELS } from "../mock";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { MatrixPasteModal, MatrixRaiseModal, MatrixHistoryModal, MatrixTraceModal, NewMatrixModal, parseGrid, gridToTsv, copyToClipboard } from "../matrixTools";
import { lookupSkyZipPrice, lookupArtZipPrice, setArtZipMatrices, setSkyZipMatrices, TRANSPARAN_FABRIC, REMOTE_PRICES } from "../artbeyPricing";
import KartelaModule from "./KartelaPage";
import { InventoryModule, ImportModule } from "./AccountingPage";
import { PricingModule } from "./CustomersPage";
import { ModuleIcon, PName, MODULE_GRADIENTS } from "../components/layout/Sidebar";
import ZenPagination from "../components/ZenPagination";
import { fetchRates, loadLogoDataUrl, fmtPrint, convAllCurrencies, printViaIframe } from "../utils/pdfUtils";
import JsBarcode from "jsbarcode";
import { BarcodeScannerModal, BarcodeResultDialog } from "../barcodeScanner";
import { ItemLabelModal } from "../itemLabels";

function ProductGroupsModule() {
  const { tr, lang } = useContext(UICtx);
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = async () => setRows((await api.get("/product-groups")).data);
  useEffect(() => { load(); }, []);
  const save = async (d) => {
    if (editing?.id) await api.put(`/product-groups/${editing.id}`, d);
    else await api.post("/product-groups", d);
    toast.success("Kaydedildi"); setEditing(null); load();
  };
  return (
    <div className="space-y-4 zen-anim-up">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-extrabold tracking-tight">{tr("Ürün Grupları & Ölçü Kuralları")}</h2>
          <p className="text-xs text-slate-500 mt-1">Her ürün için minimum/maksimum en-boy limitleri ve fiyat kuralı. AI ile de değiştirebilirsiniz.</p>
        </div>
        <button onClick={() => setEditing({})} className="zen-btn-primary"><Plus className="w-4 h-4" />{tr("Yeni Grup")}</button>
      </div>
      <div className="zen-panel overflow-hidden">
        <div className="zen-table-scroll">
        <table className="zen-table">
          <thead><tr>
            <th>{tr("Ürün Grubu")}</th><th>{tr("Mod")}</th><th className="text-right">{tr("Min m²")}</th>
            <th className="text-right">{tr("Min En")}</th><th className="text-right">{tr("Min Boy")}</th>
            <th className="text-right">{tr("Max En")}</th><th className="text-right">{tr("Max Boy")}</th>
            <th className="text-right">{tr("Yuvarlama")}</th><th className="text-right">{tr("m² Fiyat")}</th><th></th>
          </tr></thead>
          <tbody>
            {rows.map((g) => (
              <tr key={g.id}>
                <td className="font-semibold">{g.name}<div className="text-[10px] text-slate-500">{g.slug}</div></td>
                <td><span className={`zen-chip ${g.pricingMode === "matrix" ? "bg-purple-500/10 text-purple-500" : "bg-emerald-500/10 text-emerald-500"}`}>{g.pricingMode}</span></td>
                <td className="text-right zen-mono">{g.minM2}</td>
                <td className="text-right zen-mono">{g.minWidthCm}cm</td>
                <td className="text-right zen-mono">{g.minHeightCm}cm</td>
                <td className="text-right zen-mono">{g.maxWidthCm}cm</td>
                <td className="text-right zen-mono">{g.maxHeightCm}cm</td>
                <td className="text-right zen-mono">{g.roundingStepCm}cm</td>
                <td className="text-right zen-mono font-bold text-[#E5322A]">
                  {g.pricingMode === "matrix"
                    ? <span className="zen-chip bg-purple-500/10 text-purple-600 dark:text-purple-400 text-[10px]">Matris</span>
                    : g.basePricePerM2 ? `${CURRENCY_SYMBOL[g.currency]}${g.basePricePerM2}` : "—"}
                </td>
                <td><button onClick={() => setEditing(g)} className="p-1.5 hover:bg-slate-100 dark:hover:bg-[#22304A] rounded"><Pencil className="w-4 h-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
      {editing && <PGForm data={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}
function PGForm({ data, onClose, onSave }) {
  const { tr, lang } = useContext(UICtx);
  const [f, setF] = useState({ name: "", slug: "", pricingMode: "m2", minM2: 2, minWidthCm: 100, minHeightCm: 200, maxWidthCm: 500, maxHeightCm: 400, roundingStepCm: 10, basePricePerM2: 0, currency: "TL", notes: "", active: true, ...data });
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="zen-panel w-full max-w-2xl p-6 zen-anim-up">
        <div className="flex justify-between items-center mb-4"><h3 className="text-lg font-bold">{data?.id ? "Ürün Grubu Düzenle" : "Yeni Ürün Grubu"}</h3><button onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="grid md:grid-cols-2 gap-3 text-sm">
          <div><label className="block text-xs font-semibold mb-1">{tr("Ad")}</label><input className="zen-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
          <div><label className="block text-xs font-semibold mb-1">{tr("Slug (kod)")}</label><input className="zen-input" value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} /></div>
          <div><label className="block text-xs font-semibold mb-1">{tr("Fiyat Modu")}</label>
            <select className="zen-input" value={f.pricingMode} onChange={(e) => setF({ ...f, pricingMode: e.target.value })}>
              <option value="m2">{tr("m² (birim fiyat × m²)")}</option>
              <option value="matrix">{tr("Matrix (tablo)")}</option>
              <option value="adet">{tr("Adet")}</option>
            </select>
          </div>
          <div><label className="block text-xs font-semibold mb-1">{tr("Para Birimi")}</label>
            <select className="zen-input" value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })}><option>TL</option><option>EUR</option><option>USD</option></select>
          </div>
          <div><label className="block text-xs font-semibold mb-1">{tr("Min m²")}</label><input type="number" step="0.1" className="zen-input" value={f.minM2} onChange={(e) => setF({ ...f, minM2: Number(e.target.value) })} /></div>
          <div><label className="block text-xs font-semibold mb-1">{tr("Yuvarlama (cm adımı)")}</label><input type="number" className="zen-input" value={f.roundingStepCm} onChange={(e) => setF({ ...f, roundingStepCm: Number(e.target.value) })} /></div>
          <div><label className="block text-xs font-semibold mb-1">{tr("Min En (cm)")}</label><input type="number" className="zen-input" value={f.minWidthCm} onChange={(e) => setF({ ...f, minWidthCm: Number(e.target.value) })} /></div>
          <div><label className="block text-xs font-semibold mb-1">{tr("Min Boy (cm)")}</label><input type="number" className="zen-input" value={f.minHeightCm} onChange={(e) => setF({ ...f, minHeightCm: Number(e.target.value) })} /></div>
          <div><label className="block text-xs font-semibold mb-1">{tr("Max En (cm)")}</label><input type="number" className="zen-input" value={f.maxWidthCm} onChange={(e) => setF({ ...f, maxWidthCm: Number(e.target.value) })} /></div>
          <div><label className="block text-xs font-semibold mb-1">{tr("Max Boy (cm)")}</label><input type="number" className="zen-input" value={f.maxHeightCm} onChange={(e) => setF({ ...f, maxHeightCm: Number(e.target.value) })} /></div>
          {f.pricingMode !== "matrix" && (
            <div className="md:col-span-2"><label className="block text-xs font-semibold mb-1">{tr("Birim m² Fiyat (m² modunda kullanılır)")}</label><input type="number" className="zen-input" value={f.basePricePerM2} onChange={(e) => setF({ ...f, basePricePerM2: Number(e.target.value) })} /></div>
          )}
          <div className="md:col-span-2"><label className="block text-xs font-semibold mb-1">{tr("Not")}</label><textarea rows={2} className="zen-input" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
        </div>
        <div className="flex justify-end gap-2 mt-5"><button onClick={onClose} className="zen-btn-ghost">{tr("İptal")}</button><button onClick={() => onSave(f)} className="zen-btn-primary">{tr("Kaydet")}</button></div>
      </div>
    </div>
  );
}

// ============ MATRIX EDITOR ============
function MatricesModule() {
  const [list, setList] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [busy, setBusy] = useState(false);
  const [groups, setGroups] = useState([]);
  const [showPaste, setShowPaste] = useState(false);
  const [showRaise, setShowRaise] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showNew, setShowNew] = useState(false);
  useEffect(() => { api.get("/matrices").then((r) => { setList(r.data); if (r.data[0]) setSelectedId(r.data[0].id); }); }, []);
  useEffect(() => { api.get("/product-groups").then((r) => setGroups(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { if (selectedId) api.get(`/matrices/${selectedId}`).then((r) => setMatrix(r.data)); }, [selectedId]);
  const setCell = async (row, col, price) => {
    setBusy(true);
    try {
      await api.post(`/matrices/${selectedId}/cell`, { row, col, price });
      const next = { ...matrix };
      next.prices = next.prices.map((r, ri) => r.map((v, ci) => (ri === row && ci === col ? price : v)));
      setMatrix(next);
    } finally { setBusy(false); }
  };
  const applySaved = (m) => {
    setMatrix(m);
    setList((prev) => prev.map((x) => (x.id === m.id ? { ...x, ...m } : x)));
  };
  // Excel'den kopyalanan bloğu tıklanan hücreden başlayarak yerleştirir
  const pasteAt = async (ri, ci, text) => {
    const block = parseGrid(text);
    if (!block.length) return;
    if (block.length === 1 && block[0].length === 1) return; // tek hücre: normal davranış
    const next = matrix.prices.map((r) => [...r]);
    let over = 0;
    block.forEach((row, i) => row.forEach((v, j) => {
      if (next[ri + i] && next[ri + i][ci + j] !== undefined) next[ri + i][ci + j] = v;
      else over += 1;
    }));
    setBusy(true);
    try {
      const { data } = await api.post(`/matrices/${matrix.id}/bulk`, { prices: next });
      applySaved(data);
      toast.success(`${block.length} × ${block[0].length} hücre yapıştırıldı${over ? ` (${over} hücre matris dışında kaldı)` : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Yapıştırma kaydedilemedi");
    } finally { setBusy(false); }
  };
  const copyTable = async () => {
    const ok = await copyToClipboard(gridToTsv(matrix.prices));
    ok ? toast.success("Tablo panoya kopyalandı — Excel'e yapıştırabilirsiniz") : toast.error("Kopyalanamadı");
  };
  const removeMatrix = async () => {
    if (!window.confirm(`"${matrix.name}" matrisi silinsin mi?`)) return;
    await api.delete(`/matrices/${matrix.id}`);
    const rest = list.filter((x) => x.id !== matrix.id);
    setList(rest);
    setSelectedId(rest[0]?.id || null);
    setMatrix(null);
    toast.success("Matris silindi");
  };
  return (
    <div className="space-y-4 zen-anim-up">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-extrabold tracking-tight">Zip / Sineklik Fiyat Matrisi</h2>
          <p className="text-xs text-slate-500 mt-1">Hücreye tıklayıp fiyatı doğrudan girin ya da Excel'den kopyaladığınız bloğu bir hücreye <strong>Ctrl+V</strong> ile yapıştırın.</p>
        </div>
        <button onClick={() => setShowNew(true)} className="zen-btn-primary !py-2 text-xs" data-testid="matrix-new-btn">+ Yeni Matris</button>
      </div>
      <div className="flex flex-wrap gap-2">
        {list.map((m) => (
          <button key={m.id} onClick={() => setSelectedId(m.id)} className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-all ${selectedId === m.id ? "bg-[#E5322A] text-white border-[#E5322A]" : "border-slate-200 dark:border-[#22304A] hover:bg-slate-100 dark:hover:bg-[#131B2E]"}`}>
            {m.name} <span className="opacity-60">({m.currency})</span>
          </button>
        ))}
      </div>
      {matrix && (
        <div className="zen-panel p-4 overflow-x-auto">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div className="text-sm font-bold">{matrix.name} <span className="text-[11px] font-normal text-slate-500">({matrix.heights.length} boy × {matrix.widths.length} en)</span>
              {matrix.artModel && matrix.fabricFamily && (
                <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" data-testid="matrix-live-badge">
                  Sipariş formunda canlı · {matrix.artModel} / {matrix.fabricFamily}
                </span>
              )}
              {matrix.skyModel && matrix.fabricFamily && (
                <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" data-testid="matrix-live-badge">
                  Sipariş formunda canlı · {matrix.skyModel} / {matrix.fabricFamily}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setShowPaste(true)} className="zen-btn-primary !py-1.5 text-[11px]" data-testid="matrix-paste-btn">Excel'den Yapıştır</button>
              <button onClick={() => setShowRaise(true)} className="zen-btn-ghost !py-1.5 text-[11px]" data-testid="matrix-raise-btn">Toplu Zam</button>
              <button onClick={() => setShowHistory(true)} className="zen-btn-ghost !py-1.5 text-[11px]" data-testid="matrix-history-btn">Geçmiş</button>
              <button onClick={copyTable} className="zen-btn-ghost !py-1.5 text-[11px]" data-testid="matrix-copy-btn">Tabloyu Kopyala</button>
              <button onClick={removeMatrix} className="zen-btn-ghost !py-1.5 text-[11px] text-red-600" data-testid="matrix-delete-btn">Sil</button>
            </div>
          </div>
          <div className="text-[11px] text-slate-500 mb-2">Para: <strong>{matrix.currency}</strong> • Satır = Boy, kolon = En; fiyat cm ölçüsüne göre en yakın büyük hücreden okunur{matrix.noWarranty ? " • Kırmızı hücreler: garanti kapsamı dışı" : ""}</div>
          {/* İskonto Oranı */}
          <div className="flex items-center gap-3 mb-3 px-3 py-2.5 rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-700/30">
            <div>
              <div className="text-xs font-bold text-amber-800 dark:text-amber-300">Alış İskonto Oranı</div>
              <div className="text-[10px] text-amber-600 dark:text-amber-500 mt-0.5">Maliyet = Matris Fiyatı × (1 − iskonto%). Boş bırakılırsa maliyet = matris fiyatıdır.</div>
            </div>
            <div className="flex items-center gap-1 ml-auto shrink-0">
              <input
                type="number" min="0" max="99" step="0.1"
                data-testid="matrix-iskonto-orani"
                value={matrix.iskontoOrani ?? ""}
                onChange={e => setMatrix(m => ({ ...m, iskontoOrani: e.target.value === "" ? null : Number(e.target.value) }))}
                onBlur={async e => {
                  const val = e.target.value === "" ? null : Number(e.target.value);
                  try {
                    const { data } = await api.put(`/matrices/${matrix.id}`, { ...matrix, iskontoOrani: val });
                    applySaved(data);
                    toast.success("İskonto oranı kaydedildi");
                  } catch { toast.error("Kayıt hatası"); }
                }}
                placeholder="0"
                className="w-20 text-sm font-mono text-right px-2 py-1.5 rounded-lg border border-amber-300 dark:border-amber-700 bg-white dark:bg-[#131B2E] outline-none focus:ring-2 focus:ring-amber-400/40"
              />
              <span className="text-sm font-bold text-amber-700 dark:text-amber-400">%</span>
            </div>
          </div>
          <table className="text-xs zen-mono">
            <thead>
              <tr>
                <th className="p-1 text-left border-b border-r border-slate-200 dark:border-[#22304A] bg-slate-50 dark:bg-[#0C1220]">Boy ↓ / En →</th>
                {matrix.widths.map((w, ci) => (
                  <th key={ci} className="p-1 border-b border-r border-slate-200 dark:border-[#22304A] bg-slate-50 dark:bg-[#0C1220] font-bold">{w}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrix.heights.map((h, ri) => (
                <tr key={ri}>
                  <td className="p-1 border-b border-r border-slate-200 dark:border-[#22304A] bg-slate-50 dark:bg-[#0C1220] font-bold">{h}</td>
                  {matrix.widths.map((w, ci) => (
                    <td key={ci} className={`border-b border-r border-slate-200 dark:border-[#22304A] ${matrix.noWarranty?.[ri]?.[ci] ? "bg-red-100 dark:bg-red-900/40" : ""}`} title={matrix.noWarranty?.[ri]?.[ci] ? "Garanti kapsamı dışı ölçü" : undefined}>
                      <input
                        type="number" step="0.01" disabled={busy}
                        data-testid={`matrix-cell-${ri}-${ci}`}
                        value={matrix.prices[ri]?.[ci] || 0}
                        onPaste={(e) => {
                          const txt = e.clipboardData.getData("text/plain");
                          if (/[\t\n]/.test(txt)) { e.preventDefault(); pasteAt(ri, ci, txt); }
                        }}
                        onChange={(e) => {
                          const v = Number(e.target.value);
                          const next = { ...matrix };
                          next.prices = next.prices.map((r, i) => r.map((val, j) => (i === ri && j === ci ? v : val)));
                          setMatrix(next);
                        }}
                        onBlur={(e) => setCell(ri, ci, Number(e.target.value))}
                        className={`w-16 px-1 py-1 bg-transparent text-center outline-none focus:bg-[#E5322A]/10 ${matrix.noWarranty?.[ri]?.[ci] ? "text-red-700 dark:text-red-300 font-bold" : ""}`}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showPaste && matrix && (
        <MatrixPasteModal matrix={matrix} onClose={() => setShowPaste(false)} onSaved={applySaved} />
      )}
      {showRaise && matrix && (
        <MatrixRaiseModal matrix={matrix} onClose={() => setShowRaise(false)} onSaved={applySaved} />
      )}
      {showHistory && matrix && (
        <MatrixHistoryModal matrix={matrix} onClose={() => setShowHistory(false)} onRestored={applySaved} />
      )}
      {showNew && (
        <NewMatrixModal groups={groups} onClose={() => setShowNew(false)} onCreated={(m) => { setList((p) => [...p, m]); setSelectedId(m.id); }} />
      )}
    </div>
  );
}

// ============ STOK YÖNETİMİ (konsolide) ============
// ============ YEDEK PARÇA / MALZEME (bağımsız satılabilen parçalar) ============
function SparePartsModule() {
  const { tr, lang } = useContext(UICtx);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [parts, setParts] = useState([]);
  const [editingPart, setEditingPart] = useState(null);
  const [addingCat, setAddingCat] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const stock = useContext(StockSettingsCtx);

  const loadCats = async () => {
    const { data } = await api.get("/spare-part-categories");
    setCategories(data || []);
    if (!activeCat && data?.length) setActiveCat(data[0].id);
  };
  const loadParts = async (catId) => {
    if (!catId) { setParts([]); return; }
    const { data } = await api.get(`/spare-parts?categoryId=${catId}`);
    setParts(data || []);
  };
  useEffect(() => { loadCats(); }, []);
  useEffect(() => { loadParts(activeCat); }, [activeCat]);

  const addCategory = async () => {
    if (!newCatName.trim()) return;
    const { data } = await api.post("/spare-part-categories", { name: newCatName.trim(), sortOrder: categories.length });
    setNewCatName(""); setAddingCat(false);
    await loadCats();
    setActiveCat(data.id);
    toast.success("Kategori eklendi");
  };
  const renameCategory = async (c) => {
    const name = window.prompt("Kategori adı:", c.name);
    if (!name || name === c.name) return;
    await api.put(`/spare-part-categories/${c.id}`, { name });
    loadCats();
  };
  const deleteCategory = async (c) => {
    if (!window.confirm(`"${c.name}" kategorisi ve içindeki tüm parçalar silinsin mi?`)) return;
    await api.delete(`/spare-part-categories/${c.id}`);
    if (activeCat === c.id) setActiveCat(null);
    loadCats();
  };
  const deletePart = async (p) => {
    if (!window.confirm(`"${p.name}" silinsin mi?`)) return;
    await api.delete(`/spare-parts/${p.id}`);
    loadParts(activeCat);
  };
  const savePart = async (d) => {
    if (d.id) await api.put(`/spare-parts/${d.id}`, d);
    else await api.post("/spare-parts", { ...d, categoryId: activeCat });
    toast.success("Kaydedildi"); setEditingPart(null); loadParts(activeCat);
  };

  return (
    <div className="space-y-4 zen-anim-up">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h2 className="text-xl font-extrabold tracking-tight">{tr("Yedek Parça / Malzeme")}</h2>
          <p className="text-xs text-slate-500 mt-1">Motor, kumanda ve diğer bağımsız satılabilen parçaları kategoriye göre yönetin.</p>
        </div>
        <button data-testid="sparepart-new-btn" onClick={() => setEditingPart({})} disabled={!activeCat} className="zen-btn-primary disabled:opacity-40">
          <Plus className="w-4 h-4" /> Yeni Parça
        </button>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        {categories.map((c) => (
          <div key={c.id} className="group relative">
            <button data-testid={`sparepart-cat-${c.id}`} onClick={() => setActiveCat(c.id)} className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-all ${activeCat === c.id ? "bg-[#E5322A] text-white border-[#E5322A]" : "border-slate-200 dark:border-[#22304A] hover:bg-slate-100 dark:hover:bg-[#131B2E]"}`}>
              {c.name}
            </button>
            <div className="hidden group-hover:flex absolute -top-2 -right-2 gap-0.5">
              <button data-testid={`sparepart-cat-edit-${c.id}`} onClick={() => renameCategory(c)} className="w-4.5 h-4.5 rounded-full bg-slate-600 text-white flex items-center justify-center"><Pencil className="w-2.5 h-2.5" /></button>
              <button data-testid={`sparepart-cat-del-${c.id}`} onClick={() => deleteCategory(c)} className="w-4.5 h-4.5 rounded-full bg-rose-600 text-white flex items-center justify-center"><X className="w-2.5 h-2.5" /></button>
            </div>
          </div>
        ))}
        {addingCat ? (
          <div className="flex items-center gap-1">
            <input autoFocus data-testid="sparepart-cat-new-input" value={newCatName} onChange={(e) => setNewCatName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCategory()} placeholder={tr("Kategori adı")} className="zen-input !py-1.5 !text-xs !w-40" />
            <button data-testid="sparepart-cat-save" onClick={addCategory} className="zen-btn-primary !py-1.5 !px-2"><Check className="w-3.5 h-3.5" /></button>
            <button onClick={() => { setAddingCat(false); setNewCatName(""); }} className="zen-btn-ghost !py-1.5 !px-2"><X className="w-3.5 h-3.5" /></button>
          </div>
        ) : (
          <button data-testid="sparepart-cat-add-btn" onClick={() => setAddingCat(true)} className="px-3 py-2 rounded-lg text-xs font-semibold border border-dashed border-slate-300 dark:border-[#2B3B5C] text-slate-500 hover:text-[#E5322A] hover:border-[#E5322A] transition-all inline-flex items-center gap-1">
            <Plus className="w-3.5 h-3.5" /> Kategori Ekle
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {parts.map((p) => (
          <div key={p.id} data-testid={`sparepart-card-${p.id}`} className="zen-panel p-3 space-y-2">
            <div className="w-full h-24 rounded-lg bg-slate-100 dark:bg-[#0C1220] overflow-hidden flex items-center justify-center">
              {p.imageBase64 ? <img src={p.imageBase64} alt={p.name} className="w-full h-full object-cover" /> : <Package className="w-8 h-8 text-slate-400" />}
            </div>
            <div className="text-xs font-bold truncate" title={p.name}>{p.name}</div>
            <div className="text-sm font-extrabold text-[#E5322A] zen-mono">{fmt(p.price, p.currency)}</div>
            {stock.enabled && (
              <div
                className={`px-1.5 py-0.5 rounded text-[9px] font-bold border inline-flex items-center gap-1 ${(p.currentStock ?? 0) < stock.threshold ? "bg-amber-500/15 text-amber-600 border-amber-500/30" : "bg-emerald-500/10 text-emerald-600 border-emerald-500/25"}`}
                data-testid={`sparepart-stock-${p.id}`}>
                {(p.currentStock ?? 0) < stock.threshold && <AlertTriangle className="w-2.5 h-2.5" />}
                Stok: {(p.currentStock ?? 0).toFixed(p.unit === "adet" ? 0 : 1)} {p.unit || "adet"}
              </div>
            )}
            <div className="flex gap-1">
              <button data-testid={`sparepart-edit-${p.id}`} onClick={() => setEditingPart(p)} className="zen-btn-ghost !py-1 !px-2 !text-[10px] flex-1"><Pencil className="w-3 h-3" /></button>
              <button data-testid={`sparepart-del-${p.id}`} onClick={() => deletePart(p)} className="zen-btn-ghost !py-1 !px-2 !text-[10px] !text-rose-500 !border-rose-500/40"><Trash2 className="w-3 h-3" /></button>
            </div>
          </div>
        ))}
        {activeCat && parts.length === 0 && <div className="col-span-full text-center text-slate-500 text-sm py-8">{tr("Bu kategoride henüz parça yok.")}</div>}
        {!activeCat && <div className="col-span-full text-center text-slate-500 text-sm py-8">{tr("Bir kategori seçin veya yeni kategori ekleyin.")}</div>}
      </div>
      {editingPart && <SparePartForm data={editingPart} categories={categories} defaultCategoryId={activeCat} onClose={() => setEditingPart(null)} onSave={savePart} stockEnabled={stock.enabled} />}
    </div>
  );
}

function SparePartForm({ data, categories, defaultCategoryId, onClose, onSave, stockEnabled }) {
  const { tr, lang } = useContext(UICtx);
  const [f, setF] = useState({ name: "", categoryId: defaultCategoryId, price: 0, currency: "TL", kdvRate: 20, unit: "adet", currentStock: 0, imageBase64: "", active: true, ...data });
  const pickImage = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const maxW = 800;
        const scale = Math.min(1, maxW / img.width);
        const cv = document.createElement("canvas");
        cv.width = Math.round(img.width * scale); cv.height = Math.round(img.height * scale);
        cv.getContext("2d").drawImage(img, 0, 0, cv.width, cv.height);
        setF((p) => ({ ...p, imageBase64: cv.toDataURL("image/jpeg", 0.85) }));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="sparepart-form-modal">
      <div className="zen-panel w-full max-w-md p-6 zen-anim-up">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold">{data?.id ? "Parça Düzenle" : "Yeni Parça"}</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3 text-sm">
          <div>
            <label className="block text-xs font-semibold mb-1">{tr("Görsel")}</label>
            <div className="flex items-center gap-3">
              <div className="w-16 h-16 rounded-lg bg-slate-100 dark:bg-[#0C1220] overflow-hidden flex items-center justify-center shrink-0">
                {f.imageBase64 ? <img src={f.imageBase64} alt="" className="w-full h-full object-cover" /> : <Package className="w-6 h-6 text-slate-400" />}
              </div>
              <input data-testid="sparepart-image-input" type="file" accept="image/*" onChange={(e) => pickImage(e.target.files?.[0])} className="text-xs" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">{tr("Kategori")}</label>
            <select data-testid="sparepart-category-select" className="zen-input" value={f.categoryId} onChange={(e) => setF({ ...f, categoryId: e.target.value })}>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">{tr("Ad")}</label>
            <input data-testid="sparepart-name-input" className="zen-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <label className="block text-xs font-semibold mb-1">{tr("Fiyat")}</label>
              <input data-testid="sparepart-price-input" type="number" step="0.01" className="zen-input" value={f.price} onChange={(e) => setF({ ...f, price: Number(e.target.value) })} />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">{tr("Para")}</label>
              <select className="zen-input" value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })}><option>TL</option><option>EUR</option><option>USD</option></select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">{tr("KDV %")}</label>
            <input type="number" className="zen-input" value={f.kdvRate} onChange={(e) => setF({ ...f, kdvRate: Number(e.target.value) })} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-semibold mb-1">{tr("Birim")}</label>
              <select data-testid="sparepart-unit-select" className="zen-input" value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })}>
                <option value="adet">{tr("Adet")}</option>
                <option value="metre">{tr("Metre")}</option>
                <option value="top">{tr("Top")}</option>
              </select>
            </div>
            {stockEnabled && (
              <div>
                <label className="block text-xs font-semibold mb-1">{tr("Stok Miktarı")}<span className="text-slate-400">{tr("— sipariş onaylandığında düşer")}</span></label>
                <input data-testid="sparepart-stock-input" type="number" step="0.1" className="zen-input" value={f.currentStock ?? 0} onChange={(e) => setF({ ...f, currentStock: Number(e.target.value) })} />
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="zen-btn-ghost">{tr("İptal")}</button>
          <button data-testid="sparepart-save-btn" onClick={() => onSave(f)} className="zen-btn-primary">{tr("Kaydet")}</button>
        </div>
      </div>
    </div>
  );
}


function StockManagementModule() {
  const { tr, lang } = useContext(UICtx);
  const [tab, setTab] = useState("kartela");
  const stock = useContext(StockSettingsCtx);
  const tabs = [
    { id: "kartela", label: tr("Kartelalar (Kumaş/RAL/Motor)") },
    { id: "depo", label: tr("Depo & Stok") },
    { id: "spareparts", label: tr("Yedek Parça / Malzeme") },
    { id: "weights", label: tr("Ağırlık Tanımları") },
  ];
  return (
    <div className="space-y-4 zen-anim-up">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-2xl font-extrabold tracking-tight flex items-center gap-2.5"><ModuleIcon tab="stock" icon={Boxes} />{tr("Stok Yönetimi")}</h2>
        <span className="text-xs text-slate-500">{tr("Kartelalar, depo, yedek parça/malzeme ve ağırlık tanımları")}</span>
        <div className="flex-1" />
        {stock.loaded && (
          <span data-testid="stock-tracking-indicator" className={`text-xs font-semibold px-3 py-1.5 rounded-full ${stock.enabled ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400" : "bg-slate-100 text-slate-500 dark:bg-[#1A2340] dark:text-slate-400"}`}>
            {stock.enabled ? `${tr("Stok Takibi AÇIK")} (${tr("eşik")}: ${stock.threshold})` : tr("Stok Takibi KAPALI — Ayarlar > Genel'den açabilirsiniz")}
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-1 border-b border-slate-200 dark:border-[#22304A]">
        {tabs.map(x => (
          <button key={x.id} onClick={() => setTab(x.id)} data-testid={`stock-tab-${x.id}`}
            className={`px-4 py-2 text-xs font-semibold border-b-2 transition-all -mb-px ${
              tab === x.id ? "border-[#E5322A] text-[#E5322A]" : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
            }`}>{x.label}</button>
        ))}
      </div>
      {tab === "kartela" && <KartelaModule />}
      {tab === "depo" && <InventoryModule />}
      {tab === "spareparts" && <SparePartsModule />}
      {tab === "weights" && <ProductWeightsModule />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ALIŞ FİYAT LİSTELERİ MODULE
// ═══════════════════════════════════════════════════════════════════════════
function PurchaseListsModule() {
  const [discounts, setDiscounts] = useState([]);
  const [productForms, setProductForms] = useState([]);
  const [allSeries, setAllSeries] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFormSlug, setSelectedFormSlug] = useState("all");
  const [search, setSearch] = useState("");
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [purchaseEdits, setPurchaseEdits] = useState({}); // { assetId: { price, currency } }

  const savePurchasePrice = async (assetId, price, currency) => {
    const parsed = parseFloat(price);
    if (isNaN(parsed) || parsed < 0) return;
    try {
      await api.put(`/assets/${assetId}`, { purchasePrice: parsed, purchaseCurrency: currency });
      setAssets(prev => prev.map(a => a.id === assetId ? { ...a, purchasePrice: parsed, purchaseCurrency: currency } : a));
      toast.success("Alış fiyatı kaydedildi");
    } catch { toast.error("Kayıt hatası"); }
  };
  const [showAddSupplier, setShowAddSupplier] = useState(false);
  const [newSupplier, setNewSupplier] = useState({ key: "", label: "", discountPercent: 0 });
  const [saving, setSaving] = useState(false);
  const MOTOR_SLUGS = ["somfy","nice","mosel","becker","aok","novo"];

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [dRes, pfRes, seriesRes, assetsRes] = await Promise.all([
          api.get("/settings?category=catalog_discount"),
          api.get("/product-forms"),
          api.get("/fabric-series"),
          api.get("/assets"),
        ]);
        setDiscounts(dRes.data || []);
        setProductForms([{ slug: "all", name: "Tümü" }, ...(pfRes.data || [])]);
        setAllSeries(seriesRes.data || []);
        setAssets(assetsRes.data || []);
      } catch { toast.error("Veri yüklenemedi"); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const saveDiscount = async (disc, newPct) => {
    setSaving(true);
    try {
      await api.put(`/settings/${disc.id}`, { ...disc, discountPercent: parseFloat(newPct) || 0 });
      setDiscounts(p => p.map(d => d.id === disc.id ? { ...d, discountPercent: parseFloat(newPct) || 0 } : d));
      toast.success("İskonto güncellendi");
      setEditingDiscount(null);
    } catch { toast.error("Kayıt hatası"); } finally { setSaving(false); }
  };

  const addSupplier = async () => {
    if (!newSupplier.key.trim() || !newSupplier.label.trim()) { toast.error("Kod ve ad gerekli"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/settings", {
        category: "catalog_discount", key: newSupplier.key.trim().toLowerCase(),
        value: newSupplier.label.trim(), discountPercent: newSupplier.discountPercent, isActive: true,
      });
      setDiscounts(p => [...p, data]);
      setNewSupplier({ key: "", label: "", discountPercent: 0 });
      setShowAddSupplier(false);
      toast.success("Tedarikçi eklendi");
    } catch { toast.error("Ekleme hatası"); } finally { setSaving(false); }
  };

  const seriesFormMap = {};
  (allSeries || []).forEach(s => { if (s.code) seriesFormMap[s.code] = s.formSlug; });

  const filteredAssets = assets.filter(a => {
    const formSlug = seriesFormMap[a.group] || "";
    if (selectedFormSlug !== "all" && formSlug !== selectedFormSlug) return false;
    const q = search.toLowerCase();
    if (q && !((a.label || "").toLowerCase().includes(q) || (a.code || "").toLowerCase().includes(q))) return false;
    return a.price > 0;
  });

  const visibleForms = productForms.filter(f => f.slug === "all" || !MOTOR_SLUGS.includes(f.slug));

  return (
    <div className="space-y-6 zen-anim-up" data-testid="purchase-lists-module">
      {/* TEDARİKÇİ İSKONTO KARTLARI */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500">Tedarikçi İskontoları</h3>
          <button onClick={() => setShowAddSupplier(v => !v)}
            className="text-xs px-3 py-1.5 rounded-lg bg-[#E5322A] text-white font-semibold hover:bg-[#c42a22] transition">
            + Yeni Tedarikçi
          </button>
        </div>
        {showAddSupplier && (
          <div className="mb-4 p-4 rounded-xl border border-[#E5322A]/30 bg-[#E5322A]/5 grid grid-cols-3 gap-3">
            {[["Kod (slug)", "artbey", "key"], ["Tedarikçi Adı", "Artbey", "label"]].map(([lbl, ph, fld]) => (
              <div key={fld}>
                <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">{lbl}</label>
                <input className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2.5 py-1.5 text-sm outline-none focus:ring-2 focus:ring-[#E5322A]"
                  placeholder={ph} value={newSupplier[fld]} onChange={e => setNewSupplier(p => ({ ...p, [fld]: e.target.value }))} />
              </div>
            ))}
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">İskonto %</label>
              <div className="flex gap-2">
                <input type="number" min="0" max="100" className="flex-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2.5 py-1.5 text-sm outline-none focus:ring-2 focus:ring-[#E5322A]"
                  value={newSupplier.discountPercent} onChange={e => setNewSupplier(p => ({ ...p, discountPercent: parseFloat(e.target.value) || 0 }))} />
                <button onClick={addSupplier} disabled={saving} className="px-3 py-1.5 bg-[#E5322A] hover:bg-[#c42a22] text-white rounded-lg text-xs font-semibold disabled:opacity-50">Ekle</button>
              </div>
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {loading ? <div className="col-span-4 text-center py-6 text-slate-400 text-sm">Yükleniyor...</div>
            : discounts.length === 0 ? <div className="col-span-4 text-center py-6 text-slate-400 text-sm">Tedarikçi tanımlanmamış</div>
            : discounts.map(d => (
              <div key={d.id} className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 bg-white dark:bg-slate-900">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <div className="font-bold text-sm">{d.value || d.key}</div>
                    <div className="text-[10px] font-mono text-slate-400">{d.key}</div>
                  </div>
                  <button onClick={() => setEditingDiscount(editingDiscount?.id === d.id ? null : { ...d })}
                    className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400">
                    ✏️
                  </button>
                </div>
                {editingDiscount?.id === d.id ? (
                  <div className="flex gap-1.5 items-center">
                    <input type="number" min="0" max="100" defaultValue={d.discountPercent ?? 0}
                      className="w-16 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-2 py-1 text-sm font-mono text-center"
                      autoFocus id={`disc-${d.id}`} />
                    <span className="text-xs text-slate-400">%</span>
                    <button onClick={() => saveDiscount(d, document.getElementById(`disc-${d.id}`)?.value)} disabled={saving}
                      className="px-2 py-1 bg-green-600 text-white rounded text-xs disabled:opacity-50">✓</button>
                    <button onClick={() => setEditingDiscount(null)} className="px-2 py-1 bg-slate-200 dark:bg-slate-700 rounded text-xs">✕</button>
                  </div>
                ) : (
                  <div className="flex items-baseline gap-1">
                    <span className="text-2xl font-extrabold text-[#E5322A]">{d.discountPercent ?? 0}</span>
                    <span className="text-sm text-slate-400">% iskonto</span>
                  </div>
                )}
                <div className="text-[10px] text-slate-400 mt-1">
                  {(d.discountPercent ?? 0) > 0 ? `Liste × ${(100 - (d.discountPercent ?? 0)).toFixed(0)}% = alış` : "İskonto uygulanmıyor"}
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* ALIŞ FİYAT TABLOSU */}
      <div>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mr-2">Ürün Alış Fiyatları</h3>
          <div className="flex gap-1.5 flex-wrap">
            {visibleForms.map(f => (
              <button key={f.slug} onClick={() => setSelectedFormSlug(f.slug)}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold transition ${selectedFormSlug === f.slug ? "bg-[#E5322A] text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700"}`}>
                {f.name}
              </button>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-1.5">
            <svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
            <input className="bg-transparent text-xs outline-none" placeholder="Renk/kod ara..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>
        {loading ? <div className="text-center py-12 text-slate-400 text-sm">Yükleniyor...</div> : (
          <div className="overflow-x-auto rounded-2xl border border-slate-200 dark:border-slate-700">
            <table className="w-full text-xs" data-testid="purchase-price-table">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                  <th className="text-left px-4 py-3 font-semibold text-slate-500 uppercase tracking-wider">Kod</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-500 uppercase tracking-wider">Ad</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-500 uppercase tracking-wider">Grup</th>
                  <th className="text-right px-4 py-3 font-semibold text-slate-500 uppercase tracking-wider">Liste Fiyat</th>
                  {discounts.map(d => (
                    <th key={d.id} className="text-right px-4 py-3 font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                      {d.value || d.key} <span className="text-[9px] font-normal text-slate-400">({d.discountPercent ?? 0}%)</span>
                    </th>
                  ))}
                  <th className="text-right px-4 py-3 font-semibold text-slate-500 uppercase tracking-wider">Alış (Manuel)</th>
                </tr>
              </thead>
              <tbody>
                {filteredAssets.length === 0
                  ? <tr><td colSpan={5 + discounts.length} className="text-center py-10 text-slate-400">Fiyatlı ürün bulunamadı</td></tr>
                  : filteredAssets.slice(0, 200).map((a, idx) => (
                    <tr key={a.id} className={`border-b border-slate-100 dark:border-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800/30 ${idx % 2 !== 0 ? "bg-slate-50/30 dark:bg-slate-800/10" : ""}`}>
                      <td className="px-4 py-2.5 font-mono text-slate-400">{a.code || "—"}</td>
                      <td className="px-4 py-2.5 font-semibold">{a.label}</td>
                      <td className="px-4 py-2.5 text-slate-400">{seriesFormMap[a.group] || a.group || "—"}</td>
                      <td className="px-4 py-2.5 text-right font-bold font-mono">{a.price ? `${a.price.toFixed(2)} ${a.currency || ""}` : "—"}</td>
                      {discounts.map(d => {
                        const calc = a.price * (1 - ((d.discountPercent ?? 0) / 100));
                        return <td key={d.id} className="px-4 py-2.5 text-right font-mono text-emerald-600 dark:text-emerald-400">{a.price ? `${calc.toFixed(2)} ${a.currency || ""}` : "—"}</td>;
                      })}
                      <td className="px-4 py-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <input
                            type="number" min="0" step="0.01"
                            className="w-20 bg-slate-50 dark:bg-slate-800 border border-amber-200 dark:border-amber-800/40 rounded px-2 py-1 text-xs font-mono text-right text-amber-700 dark:text-amber-400 focus:ring-1 focus:ring-amber-400 outline-none"
                            placeholder="0.00"
                            defaultValue={a.purchasePrice > 0 ? a.purchasePrice.toFixed(2) : ""}
                            key={a.id}
                            onBlur={(e) => savePurchasePrice(a.id, e.target.value, (purchaseEdits[a.id]?.currency || a.purchaseCurrency || a.currency || "USD"))}
                            data-testid={`asset-purchase-price-${a.id}`}
                          />
                          <select
                            className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-1 py-1 text-[10px] text-slate-500 outline-none"
                            defaultValue={a.purchaseCurrency || a.currency || "USD"}
                            onChange={(e) => setPurchaseEdits(prev => ({ ...prev, [a.id]: { ...prev[a.id], currency: e.target.value } }))}
                            data-testid={`asset-purchase-currency-${a.id}`}
                          >
                            <option>TL</option><option>USD</option><option>EUR</option>
                          </select>
                        </div>
                      </td>
                    </tr>
                  ))}
                {filteredAssets.length > 200 && <tr><td colSpan={5 + discounts.length} className="text-center py-3 text-xs text-slate-400">+ {filteredAssets.length - 200} kayıt daha (arama ile filtreleyin)</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}



function PriceListsModule() {
  const { tr } = useContext(UICtx);
  const { user } = useContext(AuthCtx);
  const [tab, setTab] = useState("groups");

  // Bayi (dealer) rolü: sadece salt-okunur fiyat sorgulama, hiçbir yönetim sekmesi yok
  if (user?.role === "dealer") {
    return (
      <div className="space-y-4 zen-anim-up" data-testid="dealer-price-query">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-2xl font-extrabold tracking-tight flex items-center gap-2.5"><ModuleIcon tab="pricelists" icon={Layers} />{tr("Fiyat Sorgulama")}</h2>
          <span className="text-xs text-slate-500">{tr("Güncel satış fiyatlarını görüntüleyin")}</span>
        </div>
        <PricingModule readOnly />
      </div>
    );
  }

  const tabs = [
    { id: "groups", label: tr("Ürün Grupları") },
    { id: "prices", label: tr("Fiyat Listeleri") },
    { id: "matrices", label: tr("Zip/Sineklik Matrisi") },
    { id: "import", label: tr("İçeri Aktar") },
    { id: "purchaselists", label: tr("Alış Fiyat Listeleri") },
  ];
  return (
    <div className="space-y-4 zen-anim-up">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-2xl font-extrabold tracking-tight flex items-center gap-2.5"><ModuleIcon tab="pricelists" icon={Layers} />{tr("Fiyat Listeleri")}</h2>
        <span className="text-xs text-slate-500">{tr("Kategoriler, varyantlar, satış ve alış fiyat listeleri")}</span>
      </div>
      <div className="flex flex-wrap gap-1 border-b border-slate-200 dark:border-[#22304A]">
        {tabs.map(x => (
          <button key={x.id} onClick={() => setTab(x.id)} data-testid={`pricelist-tab-${x.id}`}
            className={`px-4 py-2 text-xs font-semibold border-b-2 transition-all -mb-px ${
              tab === x.id ? "border-[#E5322A] text-[#E5322A]" : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
            }`}>{x.label}</button>
        ))}
      </div>
      {tab === "groups" && <ProductGroupsModule />}
      {tab === "prices" && <PricingModule />}
      {tab === "matrices" && <MatricesModule />}
      {tab === "import" && <ImportModule />}
      {tab === "purchaselists" && <PurchaseListsModule api={api} />}
    </div>
  );
}

// ============ ÇEKİ LİSTESİ (Packing List) ============
function PackingListModule() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all"); // all | packed | notpacked
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  const [scanRaw, setScanRaw] = useState(null);
  const load = async () => {
    const r = await api.get("/orders");
    setOrders(r.data || []);
  };
  useEffect(() => { load(); }, []);

  const handleScanned = (raw) => {
    setShowScanner(false);
    setScanRaw(raw);
  };

  const handleAddToPackingList = async (order, newBoxes) => {
    // Mevcut çeki listesine ekle ya da yenisini oluştur
    const existing = order.packingList?.boxes || [];
    const startNo = existing.length + 1;
    const boxes = [...existing, ...newBoxes.map((b, i) => ({ ...b, no: startNo + i }))];
    try {
      await api.put(`/orders/${order.id}/packing-list`, { boxes });
      setSelected({ ...order, packingList: { ...(order.packingList || {}), boxes } });
      load();
    } catch (e) { toast.error("Kaydedilemedi"); }
  };
  const filtered = orders.filter(o => {
    const hasPack = !!o.packingList;
    if (filter === "packed" && !hasPack) return false;
    if (filter === "notpacked" && hasPack) return false;
    const q = query.toLowerCase();
    return !q || (ono(o) + " " + o.id + " " + (o.customerName || "") + " " + (o.projectName || "")).toLowerCase().includes(q);
  });
  return (
    <div className="space-y-4 zen-anim-up">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-2xl font-extrabold tracking-tight flex items-center gap-2.5"><ModuleIcon tab="checks" icon={ClipboardList} /> Çeki Listesi</h2>
        <span className="text-xs text-slate-500">Sipariş bazlı koli ölçüsü, net & brüt ağırlık takibi</span>
        <div className="flex-1" />
        <button
          onClick={() => setShowScanner(true)}
          data-testid="packing-scan-btn"
          className="zen-btn-primary !text-xs"
        >
          <Camera className="w-3.5 h-3.5" /> Barkod ile Tara
        </button>
        <div className="flex items-center gap-2 zen-panel px-3">
          <Search className="w-4 h-4 text-slate-400" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Sipariş no, müşteri..." className="bg-transparent outline-none text-sm py-1.5 w-56" />
        </div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="zen-input !py-1.5 !w-auto">
          <option value="all">Tümü</option>
          <option value="notpacked">Çeki listesi yok</option>
          <option value="packed">Çeki listesi hazır</option>
        </select>
      </div>
      <div className="zen-panel !p-0 overflow-hidden">
        <table className="zen-table">
          <thead>
            <tr>
              <th>Sipariş No</th>
              <th>Cari</th>
              <th>Proje</th>
              <th className="text-center">Kalem</th>
              <th className="text-center">Koli</th>
              <th className="text-right">Net (kg)</th>
              <th className="text-right">Brüt (kg)</th>
              <th className="text-center">Durum</th>
              <th className="text-center">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(o => (
              <tr key={o.id}>
                <td className="zen-mono font-bold text-[#E5322A]">{ono(o)}</td>
                <td className="font-semibold text-sm">{o.customerName}</td>
                <td className="text-xs text-slate-500">{o.projectName || "-"}</td>
                <td className="text-center zen-mono">{(o.items || []).length}</td>
                <td className="text-center zen-mono">{o.packingList?.totalBoxes ?? "-"}</td>
                <td className="text-right zen-mono">{o.packingList?.totalNetKg?.toFixed(2) ?? "-"}</td>
                <td className="text-right zen-mono font-bold">{o.packingList?.totalGrossKg?.toFixed(2) ?? "-"}</td>
                <td className="text-center">
                  {o.packingList ? <span className="zen-chip bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 border-emerald-500/30">Hazır</span>
                                 : <span className="zen-chip bg-slate-500/15 text-slate-500 border-slate-500/30">Yok</span>}
                </td>
                <td className="text-center">
                  <button onClick={() => setSelected(o)} data-testid={`packing-open-${o.id}`} className="zen-btn-primary !text-xs !py-1.5"><Pencil className="w-3.5 h-3.5" /> Düzenle / Oluştur</button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-slate-500 text-sm">Sipariş bulunamadı.</td></tr>}
          </tbody>
        </table>
      </div>
      {selected && <PackingListDialog order={selected} onClose={() => { setSelected(null); load(); }} />}
      {showScanner && <BarcodeScannerModal onClose={() => setShowScanner(false)} onScanned={handleScanned} />}
      {scanRaw && <BarcodeResultDialog raw={scanRaw} onClose={() => setScanRaw(null)} onAddToPackingList={handleAddToPackingList} />}
    </div>
  );
}

function PackingListDialog({ order, onClose }) {
  const [boxes, setBoxes] = useState(order.packingList?.boxes || []);
  const [loading, setLoading] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [scanRaw, setScanRaw] = useState(null);
  const [showItemLabels, setShowItemLabels] = useState(false);
  const autoGen = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/packing-list/auto`);
      setBoxes(data.boxes || []);
      toast.success("Çeki listesi otomatik oluşturuldu");
    } catch (e) { toast.error("Hata: " + (e?.response?.data?.detail || e.message)); }
    setLoading(false);
  };
  const save = async () => {
    setLoading(true);
    try {
      await api.put(`/orders/${order.id}/packing-list`, { boxes });
      toast.success("Kaydedildi");
      onClose();
    } catch (e) { toast.error("Hata: " + (e?.response?.data?.detail || e.message)); }
    setLoading(false);
  };
  const upd = (i, k, v) => {
    const nb = [...boxes]; nb[i] = { ...nb[i], [k]: v }; setBoxes(nb);
  };
  const addBox = () => setBoxes([...boxes, { no: boxes.length + 1, widthCm: 20, heightCm: 20, depthCm: 100, netKg: 0, grossKg: 0, pieces: 1, content: "", note: "" }]);
  const removeBox = (i) => setBoxes(boxes.filter((_, idx) => idx !== i));
  const totalNet = boxes.reduce((s, b) => s + Number(b.netKg || 0), 0);
  const totalGross = boxes.reduce((s, b) => s + Number(b.grossKg || 0), 0);
  const totalPieces = boxes.reduce((s, b) => s + Number(b.pieces || 0), 0);

  // Koli etiketleri PDF - her koli için ayrı sayfa (100x150mm etiket)
  // Firma bilgileri + Ürün detayı + Ürün çeşidi + Son müşteri + Barkod
  const printBoxLabels = () => {
    let html = '';
    const items = order.items || [];
    const boxCards = boxes.map((b, i) => {
      // Sipariş kalemleri ile eşleştir (ilk zip/stor vb.)
      const relatedItem = items.find(it => (b.content || "").toLowerCase().includes((it.product || "").toLowerCase().slice(0, 6))) || items[0] || {};
      const productType = relatedItem.product || (b.content || "").split("(")[0].trim();
      const fabric = relatedItem.fabric || "";
      const color = relatedItem.color || "";
      const ralCode = relatedItem.ralCode || relatedItem.ralColor || "";
      const detailParts = [
        b.widthCm && b.heightCm ? `${b.widthCm}×${b.heightCm}×${b.depthCm}cm` : "",
        fabric,
        color,
        ralCode ? `RAL ${ralCode}` : "",
      ].filter(Boolean).join(" · ");
      // Code128 barcode via JsBarcode into a canvas → data URL
      let barcodeUrl = "";
      try {
        const c = document.createElement("canvas");
        JsBarcode(c, `${ono(order)}-K${i + 1}`, { format: "CODE128", displayValue: true, height: 55, fontSize: 12, margin: 2 });
        barcodeUrl = c.toDataURL("image/png");
      } catch (e) {}
      return `
      <div class="label">
        <div class="lbl-hdr">
          <div class="lbl-brand">
            <div class="lbl-logo">ZEN</div>
            <div class="lbl-sub">MEKANİZMALI PERDE SİSTEMLERİ</div>
          </div>
          <div class="lbl-cnt">
            <div class="lbl-cnt-line">📞 +90 (212) 555 0192</div>
            <div class="lbl-cnt-line">✉ info@zenperde.com</div>
            <div class="lbl-cnt-line">🌐 www.zenperde.com</div>
          </div>
        </div>

        <div class="lbl-koli">
          <div class="lbl-koli-lbl">KOLİ NO</div>
          <div class="lbl-koli-val">${i + 1} / ${boxes.length}</div>
        </div>

        <div class="lbl-block">
          <div class="lbl-k">Sipariş No</div>
          <div class="lbl-v">${(ono(order) || "-").replace(/</g, "&lt;")}</div>
        </div>

        <div class="lbl-block">
          <div class="lbl-k">Bayi / Müşteri</div>
          <div class="lbl-v">${(order.customerName || "-").replace(/</g, "&lt;")}</div>
        </div>

        <div class="lbl-block-hi">
          <div class="lbl-k-hi">SON MÜŞTERİ / TESLİMAT</div>
          <div class="lbl-v-hi">${(order.shipToName || order.projectName || "-").replace(/</g, "&lt;")}</div>
        </div>

        <div class="lbl-block">
          <div class="lbl-k">Ürün Çeşidi</div>
          <div class="lbl-v">${productType.replace(/</g, "&lt;")}</div>
        </div>

        <div class="lbl-block">
          <div class="lbl-k">Ürün Detayı</div>
          <div class="lbl-v">${detailParts || (b.note || "-").replace(/</g, "&lt;")}</div>
        </div>

        <div class="lbl-grid">
          <div>
            <div class="lbl-mini-k">Kutu Ölçüsü</div>
            <div class="lbl-mini-v">${b.widthCm}×${b.heightCm}×${b.depthCm} cm</div>
          </div>
          <div>
            <div class="lbl-mini-k">Adet</div>
            <div class="lbl-mini-v">${b.pieces || 1}</div>
          </div>
          <div>
            <div class="lbl-mini-k">Brüt Ağırlık</div>
            <div class="lbl-mini-v">${Number(b.grossKg || 0).toFixed(2)} kg</div>
          </div>
        </div>

        <div class="lbl-barcode">
          ${barcodeUrl ? `<img src="${barcodeUrl}" alt="barcode">` : ""}
        </div>
      </div>`;
    }).join("");

    html = `
<!DOCTYPE html><html lang="tr"><head><meta charset="utf-8"><title>Koli Etiketleri ${order.id}</title>
<style>
  @page { size: 100mm 150mm; margin: 3mm; }
  * { box-sizing: border-box; }
  body { font-family: 'Segoe UI', 'Roboto', Arial, sans-serif; color: #111; margin: 0; }
  .label { width: 94mm; height: 144mm; padding: 3mm; border: 2px solid #E5322A; border-radius: 4px; display: flex; flex-direction: column; page-break-after: always; overflow: hidden; }
  .label:last-child { page-break-after: auto; }
  .lbl-hdr { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 2mm; border-bottom: 1px solid #E5322A; margin-bottom: 2mm; }
  .lbl-brand { display: flex; flex-direction: column; }
  .lbl-logo { background: #E5322A; color: #fff; width: 18mm; height: 8mm; display: flex; align-items: center; justify-content: center; font-size: 14pt; font-weight: 800; letter-spacing: 2px; border-radius: 2px; }
  .lbl-sub { font-size: 6pt; font-weight: 700; letter-spacing: 0.5px; color: #333; margin-top: 1mm; }
  .lbl-cnt { text-align: right; font-size: 6.5pt; line-height: 1.4; color: #555; }
  .lbl-koli { background: #1E293B; color: #fff; padding: 2mm 3mm; border-radius: 3px; display: flex; justify-content: space-between; align-items: center; margin-bottom: 2mm; }
  .lbl-koli-lbl { font-size: 7pt; letter-spacing: 2px; opacity: 0.85; }
  .lbl-koli-val { font-size: 14pt; font-weight: 800; }
  .lbl-block { margin-bottom: 1.5mm; }
  .lbl-k { font-size: 6.5pt; text-transform: uppercase; letter-spacing: 1px; color: #888; }
  .lbl-v { font-size: 9pt; font-weight: 600; color: #111; word-break: break-word; }
  .lbl-block-hi { background: #FEF3C7; border-left: 3px solid #F59E0B; padding: 2mm 3mm; margin: 2mm 0; border-radius: 2px; }
  .lbl-k-hi { font-size: 6.5pt; text-transform: uppercase; letter-spacing: 1px; color: #92400E; font-weight: 700; }
  .lbl-v-hi { font-size: 11pt; font-weight: 800; color: #78350F; margin-top: 0.5mm; word-break: break-word; }
  .lbl-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 2mm; margin: 1.5mm 0; padding: 1.5mm; background: #F5F5F7; border-radius: 3px; }
  .lbl-mini-k { font-size: 5.5pt; text-transform: uppercase; letter-spacing: 0.5px; color: #666; }
  .lbl-mini-v { font-size: 8pt; font-weight: 700; color: #111; }
  .lbl-barcode { margin-top: auto; padding-top: 2mm; text-align: center; }
  .lbl-barcode img { max-width: 100%; height: 14mm; }
</style></head>
<body>
  ${boxCards}
</body></html>`;
    printViaIframe(html, false);
    toast.success(`${boxes.length} koli etiketi yazıcıya gönderildi`);
  };

  const printList = () => {
    let html = '';
    const today = new Date().toLocaleDateString("tr-TR");
    const rows = boxes.map((b, i) => {
      const content = (b.content || "").replace(/</g, "&lt;");
      const note = (b.note || "").replace(/</g, "&lt;");
      // Derive package type + material from content string
      const lc = content.toLowerCase();
      const pkgType = lc.includes("dikme") ? "Rulo Karton" : "Karton Koli";
      const material = lc.includes("sky") ? "Alüminyum + Kumaş" : lc.includes("zip") ? "Alüminyum + Kumaş" : lc.includes("stor") ? "Alüminyum + Kumaş" : "Karışık";
      const qty = `${b.pieces || 1} Adet`;
      return `<tr>
        <td class="c b">${b.no ?? i + 1}</td>
        <td>${pkgType}</td>
        <td>${content}${note ? `<div class="note">${note}</div>` : ""}</td>
        <td>${material}</td>
        <td class="c">${qty}</td>
        <td class="r">${Number(b.grossKg || 0).toFixed(2)}</td>
        <td class="r">${Number(b.netKg || 0).toFixed(2)}</td>
        <td class="c">${b.widthCm || 0}×${b.heightCm || 0}×${b.depthCm || 0}</td>
      </tr>`;
    }).join("");

    html = `
<!DOCTYPE html><html lang="tr"><head>
<meta charset="utf-8">
<title>Çeki Listesi ${order.id}</title>
<style>
  @page { size: A4 portrait; margin: 14mm 12mm; }
  * { box-sizing: border-box; }
  body { font-family: 'Segoe UI', Arial, sans-serif; color: #1a1a1a; margin: 0; font-size: 11px; }
  /* header */
  .top-bar { display: flex; align-items: flex-start; justify-content: space-between; padding-bottom: 10px; border-bottom: 2px solid #E5322A; }
  .brand { text-align: center; flex: 1; }
  .brand .logo { display: inline-flex; align-items: center; justify-content: center; width: 78px; height: 78px; background: #E5322A; border-radius: 10px; margin-bottom: 4px; }
  .brand .logo span { color: #fff; font-weight: 800; font-size: 28px; letter-spacing: 2px; }
  .brand .tag { font-size: 10px; letter-spacing: 3px; color: #444; font-weight: 600; text-transform: uppercase; }
  .top-bar .date { text-align: right; font-size: 12px; color: #333; font-weight: 600; padding-top: 6px; min-width: 140px; }
  .top-bar .date small { display:block; font-size: 9px; color:#888; font-weight:500; text-transform:uppercase; letter-spacing:1px; }
  /* title */
  .title { text-align: center; margin: 14px 0 6px; }
  .title h1 { margin: 0; font-size: 22px; font-weight: 800; letter-spacing: 8px; color: #1a1a1a; }
  .title h2 { margin: 2px 0 0; font-size: 12px; font-weight: 500; letter-spacing: 4px; color: #666; }
  /* info block */
  .info { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 24px; margin: 12px 0 6px; padding: 10px 12px; background: #F5F5F7; border: 1px solid #E5E5EA; border-radius: 4px; }
  .info .row { display: flex; align-items: flex-start; gap: 8px; font-size: 11px; }
  .info .k { min-width: 132px; font-weight: 700; color: #333; }
  .info .k small { display:block; font-size: 8.5px; color: #999; font-weight:500; text-transform: uppercase; letter-spacing: 0.5px; }
  .info .v { flex: 1; color: #111; font-weight: 500; word-break: break-word; }
  /* table */
  table.plist { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 10.5px; }
  table.plist th { background: #E5322A; color: #fff; text-align: center; padding: 6px 5px; border: 1px solid #B0221D; font-weight: 700; }
  table.plist th small { display: block; font-size: 9px; font-weight: 500; opacity: 0.85; margin-top: 1px; letter-spacing: 0.2px; }
  table.plist td { border: 1px solid #C8C8CE; padding: 6px 6px; vertical-align: middle; }
  table.plist tr:nth-child(even) td { background: #FAFAFB; }
  table.plist td.c { text-align: center; }
  table.plist td.r { text-align: right; }
  table.plist td.b { font-weight: 700; }
  table.plist td .note { font-size: 9px; color: #666; margin-top: 2px; font-style: italic; }
  tr.total td { background: #1E293B !important; color: #fff; font-weight: 800; font-size: 11px; border-color: #0F172A; padding: 8px 6px; }
  tr.total td.tot-label { text-align: right; letter-spacing: 1px; }
  /* footer */
  .footer { margin-top: 18px; display: grid; grid-template-columns: 1fr 1fr; gap: 40px; font-size: 10.5px; }
  .footer .sig { text-align: center; padding-top: 40px; border-top: 1px solid #C8C8CE; font-weight: 600; color: #333; }
  .footer .sig small { display: block; color: #888; font-weight: 400; font-size: 9px; margin-top: 3px; }
  .meta { margin-top: 8px; font-size: 9px; color: #999; text-align: right; }
</style>
</head><body>
  <div class="top-bar">
    <div style="min-width:140px"></div>
    <div class="brand">
      <div class="logo"><span>ZEN</span></div>
      <div class="tag">Mekanizmalı Perde Sistemleri</div>
    </div>
    <div class="date">
      <small>Tarih / Date</small>
      ${today}
    </div>
  </div>

  <div class="title">
    <h1>ÇEKİ LİSTESİ</h1>
    <h2>PACKING LIST</h2>
  </div>

  <div class="info">
    <div class="row">
      <div class="k">Alıcı<small>Consignee</small></div>
      <div class="v">${(order.customerName || "-").replace(/</g, "&lt;")}</div>
    </div>
    <div class="row">
      <div class="k">Fatura No.<small>Invoice No.</small></div>
      <div class="v">${(order.invoiceNo || order.id || "-").replace(/</g, "&lt;")}</div>
    </div>
    <div class="row">
      <div class="k">Sipariş No.<small>Order No.</small></div>
      <div class="v">${(order.id || "-").replace(/</g, "&lt;")}</div>
    </div>
    <div class="row">
      <div class="k">Proje<small>Project</small></div>
      <div class="v">${(order.projectName || "-").replace(/</g, "&lt;")}</div>
    </div>
  </div>

  <table class="plist">
    <thead>
      <tr>
        <th style="width:6%">Paket No.<small>Package No.</small></th>
        <th style="width:11%">Paket Türü<small>Package Type</small></th>
        <th style="width:28%">Muhteviyat<small>Contents</small></th>
        <th style="width:14%">Malzeme<small>Material</small></th>
        <th style="width:9%">Miktar<small>Quantity</small></th>
        <th style="width:10%">Brüt (Kg)<small>Gross Weight</small></th>
        <th style="width:10%">Net (Kg)<small>Net Weight</small></th>
        <th style="width:12%">Ölçü (Cm)<small>Dimensions</small></th>
      </tr>
    </thead>
    <tbody>
      ${rows}
      <tr class="total">
        <td colspan="4" class="tot-label">TOPLAM · TOTAL</td>
        <td class="c">${totalPieces} Adet / ${boxes.length} Paket</td>
        <td class="r">${totalGross.toFixed(2)}</td>
        <td class="r">${totalNet.toFixed(2)}</td>
        <td></td>
      </tr>
    </tbody>
  </table>

  <div class="footer">
    <div class="sig">Hazırlayan<small>Prepared by</small></div>
    <div class="sig">Teslim Alan<small>Received by</small></div>
  </div>
  <div class="meta">ZEN Perde · www.zenperde.com · info@zenperde.com · Yazdırma: ${new Date().toLocaleString("tr-TR")}</div>
</body></html>`;
    printViaIframe(html, false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="zen-panel w-full max-w-6xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-200 dark:border-[#22304A] flex items-center justify-between sticky top-0 bg-white dark:bg-[#131B2E] z-10">
          <div>
            <h3 className="text-lg font-bold">Çeki Listesi · <span className="text-[#E5322A] zen-mono">{order.id}</span></h3>
            <div className="text-xs text-slate-500">{order.customerName} · {order.projectName || "-"} · {(order.items || []).length} kalem</div>
          </div>
          <div className="flex gap-2">
            <button onClick={autoGen} disabled={loading} className="zen-btn-ghost !text-xs" data-testid="packing-auto"><RefreshCw className="w-3.5 h-3.5" /> Otomatik Oluştur</button>
            <button onClick={() => setShowScanner(true)} className="zen-btn-ghost !text-xs" data-testid="packing-scan-dialog"><Camera className="w-3.5 h-3.5" /> Barkod Tara</button>
            <button onClick={() => setShowItemLabels(true)} className="zen-btn-primary !text-xs" data-testid="packing-item-labels"><Tag className="w-3.5 h-3.5" /> Kalem Etiketleri</button>
            <button onClick={printList} disabled={boxes.length === 0} className="zen-btn-ghost !text-xs" data-testid="packing-print"><Printer className="w-3.5 h-3.5" /> Çeki Listesi Yazdır</button>
            <button onClick={printBoxLabels} disabled={boxes.length === 0} className="zen-btn-primary !text-xs" data-testid="packing-labels"><FileText className="w-3.5 h-3.5" /> Koli Etiketleri</button>
            <button onClick={onClose}><X className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="p-5 space-y-4">
          {boxes.length === 0 && (
            <div className="zen-panel p-8 text-center">
              <ClipboardList className="w-12 h-12 mx-auto text-slate-400 mb-3" />
              <div className="text-sm text-slate-500 mb-4">Henüz çeki listesi yok. "Otomatik Oluştur" ile ağırlık tanımlarından hesaplayın ya da elle ekleyin.</div>
              <button onClick={autoGen} className="zen-btn-primary" data-testid="packing-auto-empty"><RefreshCw className="w-4 h-4" /> Otomatik Oluştur</button>
            </div>
          )}
          {boxes.length > 0 && (
            <>
              <table className="zen-table !text-xs">
                <thead><tr>
                  <th className="w-12">Koli #</th>
                  <th>İçerik</th>
                  <th className="w-24">En (cm)</th>
                  <th className="w-24">Boy (cm)</th>
                  <th className="w-24">Derinlik (cm)</th>
                  <th className="w-20">Adet</th>
                  <th className="w-24">Net (kg)</th>
                  <th className="w-24">Brüt (kg)</th>
                  <th>Not</th>
                  <th className="w-10"></th>
                </tr></thead>
                <tbody>
                  {boxes.map((b, i) => (
                    <tr key={i}>
                      <td className="zen-mono font-bold">{i + 1}</td>
                      <td><input className="zen-input !py-1 !text-xs" value={b.content || ""} onChange={(e) => upd(i, "content", e.target.value)} /></td>
                      <td><input type="number" className="zen-input !py-1 !text-xs !text-right" value={b.widthCm || ""} onChange={(e) => upd(i, "widthCm", Number(e.target.value))} /></td>
                      <td><input type="number" className="zen-input !py-1 !text-xs !text-right" value={b.heightCm || ""} onChange={(e) => upd(i, "heightCm", Number(e.target.value))} /></td>
                      <td><input type="number" className="zen-input !py-1 !text-xs !text-right" value={b.depthCm || ""} onChange={(e) => upd(i, "depthCm", Number(e.target.value))} /></td>
                      <td><input type="number" className="zen-input !py-1 !text-xs !text-center" value={b.pieces || ""} onChange={(e) => upd(i, "pieces", Number(e.target.value))} /></td>
                      <td><input type="number" step="0.01" data-testid={`box-net-${i}`} className="zen-input !py-1 !text-xs !text-right" value={b.netKg || ""} onChange={(e) => upd(i, "netKg", Number(e.target.value))} /></td>
                      <td><input type="number" step="0.01" data-testid={`box-gross-${i}`} className="zen-input !py-1 !text-xs !text-right" value={b.grossKg || ""} onChange={(e) => upd(i, "grossKg", Number(e.target.value))} /></td>
                      <td><input className="zen-input !py-1 !text-xs" value={b.note || ""} onChange={(e) => upd(i, "note", e.target.value)} /></td>
                      <td><button onClick={() => removeBox(i)} className="p-1 text-rose-500 hover:bg-rose-500/10 rounded"><Trash2 className="w-3.5 h-3.5" /></button></td>
                    </tr>
                  ))}
                  <tr className="bg-slate-100 dark:bg-[#0C1220] font-bold">
                    <td colSpan={5} className="text-right">TOPLAM ({boxes.length} koli)</td>
                    <td className="text-center zen-mono">{totalPieces}</td>
                    <td className="text-right zen-mono">{totalNet.toFixed(2)}</td>
                    <td className="text-right zen-mono">{totalGross.toFixed(2)}</td>
                    <td colSpan={2}></td>
                  </tr>
                </tbody>
              </table>
              <button onClick={addBox} className="zen-btn-ghost !text-xs"><Plus className="w-3.5 h-3.5" /> Yeni Koli Ekle</button>
            </>
          )}
        </div>
        <div className="p-5 border-t border-slate-200 dark:border-[#22304A] flex justify-end gap-2 sticky bottom-0 bg-white dark:bg-[#131B2E]">
          <button onClick={onClose} className="zen-btn-ghost">Vazgeç</button>
          <button onClick={save} disabled={loading || boxes.length === 0} className="zen-btn-primary" data-testid="packing-save"><CheckCircle2 className="w-4 h-4" /> Kaydet</button>
        </div>
      </div>
      {showScanner && <BarcodeScannerModal onClose={() => setShowScanner(false)} onScanned={(raw) => { setShowScanner(false); setScanRaw(raw); }} />}
      {scanRaw && (
        <BarcodeResultDialog
          raw={scanRaw}
          onClose={() => setScanRaw(null)}
          onAddToPackingList={(_, newBoxes) => {
            setBoxes(prev => [...prev, ...newBoxes.map((b, i) => ({ ...b, no: prev.length + i + 1 }))]);
            setScanRaw(null);
          }}
        />
      )}
      {showItemLabels && <ItemLabelModal order={order} onClose={() => setShowItemLabels(false)} />}
    </div>
  );
}

// ============ ÜRÜN AĞIRLIK TANIMLARI (Stok Yönetimi altı) ============
function ProductWeightsModule() {
  const [groups, setGroups] = useState([]);
  const [weights, setWeights] = useState({}); // slug -> row
  const [editing, setEditing] = useState(null);
  const load = async () => {
    const [g, w] = await Promise.all([api.get("/product-groups"), api.get("/product-weights")]);
    setGroups(g.data || []);
    const m = {}; (w.data || []).forEach(x => m[x.productGroupSlug] = x);
    setWeights(m);
  };
  useEffect(() => { load(); }, []);
  const save = async (slug, data) => {
    await api.put(`/product-weights/${slug}`, data);
    toast.success("Kaydedildi"); setEditing(null); load();
  };
  const FIELDS = [
    ["fabricKgPerM2", "Kumaş kg/m²", 0.5],
    ["topKasaKgPerM", "Üst Kasa kg/m", 0],
    ["dikmeKgPerM", "Dikme kg/m", 0],
    ["boruKgPerM", "Boru kg/m", 0],
    ["aksesuarKgFixed", "Aksesuar sabit kg", 0],
    ["motorKg", "Motor kg", 4.5],
    ["controlKg", "Kumanda kg", 0.2],
    ["boxKg", "Koli boş kg", 0.5],
    ["extraKgFixed", "Ekstra kg", 0],
  ];
  return (
    <div className="space-y-3">
      <div className="text-xs text-slate-500">
        Her ürün grubu için parça bazlı ağırlıklar. Zip perdeler için: Üst Kasa (en·kg/m), Dikme (boy·kg/m), Boru (en·kg/m), Aksesuar sabit, Kumaş m² ile hesaplanır. Çeki listesi "Otomatik Oluştur" bu değerleri kullanır.
      </div>
      <div className="zen-panel !p-0 overflow-x-auto">
        <table className="zen-table !text-[11px] min-w-max">
          <thead><tr>
            <th className="min-w-[180px]">Ürün Grubu</th>
            {FIELDS.map(([k, lbl]) => <th key={k} className="text-right px-2">{lbl}</th>)}
            <th className="text-center px-2">Std Koli (EN×BOY×DER)</th>
            <th className="text-center">İşlem</th>
          </tr></thead>
          <tbody>
            {groups.map(g => {
              const w = weights[g.slug] || {};
              const isE = editing === g.slug;
              return (
                <tr key={g.slug}>
                  <td className="font-semibold whitespace-nowrap">{g.name}</td>
                  {FIELDS.map(([k, lbl, def]) => (
                    <td key={k} className="text-right">
                      {isE ? (
                        <input type="number" step="0.001" className="zen-input !py-1 !text-[11px] !text-right w-20" defaultValue={w[k] ?? def} data-testid={`pw-${g.slug}-${k}`} />
                      ) : (
                        <span className="zen-mono">{Number(w[k] ?? def).toFixed(2)}</span>
                      )}
                    </td>
                  ))}
                  <td>
                    {isE ? (
                      <div className="flex gap-1">
                        <input type="number" className="zen-input !py-1 !text-[11px] !text-center w-12" defaultValue={w.defaultBoxWidthCm ?? 20} data-testid={`pw-${g.slug}-bw`} />
                        <input type="number" className="zen-input !py-1 !text-[11px] !text-center w-12" defaultValue={w.defaultBoxHeightCm ?? 20} data-testid={`pw-${g.slug}-bh`} />
                        <input type="number" className="zen-input !py-1 !text-[11px] !text-center w-12" defaultValue={w.defaultBoxDepthCm ?? 200} data-testid={`pw-${g.slug}-bd`} />
                      </div>
                    ) : (
                      <span className="text-center zen-mono">{w.defaultBoxWidthCm ?? 20}×{w.defaultBoxHeightCm ?? 20}×{w.defaultBoxDepthCm ?? 200}</span>
                    )}
                  </td>
                  <td className="text-center">
                    {isE ? (
                      <button onClick={() => {
                        const gid = g.slug;
                        const data = {};
                        FIELDS.forEach(([k]) => {
                          data[k] = Number(document.querySelector(`[data-testid="pw-${gid}-${k}"]`).value) || 0;
                        });
                        data.defaultBoxWidthCm = Number(document.querySelector(`[data-testid="pw-${gid}-bw"]`).value) || 0;
                        data.defaultBoxHeightCm = Number(document.querySelector(`[data-testid="pw-${gid}-bh"]`).value) || 0;
                        data.defaultBoxDepthCm = Number(document.querySelector(`[data-testid="pw-${gid}-bd"]`).value) || 0;
                        save(gid, data);
                      }} className="zen-btn-primary !text-[10px] !py-1"><CheckCircle2 className="w-3 h-3" /> Kaydet</button>
                    ) : (
                      <button onClick={() => setEditing(g.slug)} className="zen-btn-ghost !text-[10px] !py-1"><Pencil className="w-3 h-3" /> Düzenle</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============ FATURA & İRSALİYE ============
function InvoicesModule({ subTab }) {
  const [type, setType] = useState(subTab || "satis"); // satis | alis | satis_iade | alis_iade | irsaliye
  const [invoices, setInvoices] = useState([]);
  const [invPage, setInvPage] = useState(1);
  const [invPageSize, setInvPageSize] = useState(25);
  useEffect(() => { setInvPage(1); }, [type]);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  const TYPES = [
    { id: "satis",      label: "Satış Faturaları",  icon: FileText, color: "emerald" },
    { id: "alis",       label: "Alış Faturaları",    icon: Package,  color: "purple" },
    { id: "satis_iade", label: "Satış İadeleri",     icon: RefreshCw, color: "amber" },
    { id: "alis_iade",  label: "Alış İadeleri",       icon: Trash2,   color: "rose" },
    { id: "irsaliye",   label: "İrsaliyeler",         icon: Truck,    color: "sky" },
  ];

  const load = async () => {
    setLoading(true);
    try {
      const [inv, ord, cus] = await Promise.all([
        api.get(`/invoices?type=${type}`),
        api.get("/orders"),
        api.get("/customers"),
      ]);
      setInvoices(inv.data); setOrders(ord.data); setCustomers(cus.data);
    } catch (e) { toast.error("Yüklenemedi"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [type]);

  const del = async (id) => {
    if (!window.confirm("Fatura silinsin mi? Cari bakiye etkisi geri alınır.")) return;
    try {
      await api.delete(`/invoices/${id}`);
      toast.success("Silindi"); load();
    } catch (e) { toast.error("Silinemedi"); }
  };

  const printInvoice = async (inv) => {
    const rates = await fetchRates();
    const logoUrl = await loadLogoDataUrl();
    const rows = (inv.items || []).map((it, i) => `
      <tr>
        <td class="c b">${i + 1}</td>
        <td>${(it.product || "-").replace(/</g, "&lt;")}</td>
        <td class="c">${it.qty || 1}</td>
        <td class="r">${fmtPrint(it.unitPrice, inv.currency)}</td>
        <td class="c">%${it.kdvRate ?? 20}</td>
        <td class="r b">${fmtPrint(it.lineTotal, inv.currency)}</td>
      </tr>`).join("");
    const totalConv = convAllCurrencies(inv.total, inv.currency, rates);
    const typeLbl = { satis: "SATIŞ FATURASI", alis: "ALIŞ FATURASI", satis_iade: "SATIŞ İADE FATURASI", alis_iade: "ALIŞ İADE FATURASI", irsaliye: "SEVK İRSALİYESİ" }[inv.type];

    // Tevkifat hesabı
    const isTevkifat = inv.invoiceCategory === "tevkifatli" && inv.tevkifatAmount > 0;
    const tevkifatRate = inv.tevkifatRate || "";
    const tevkifatAmount = Number(inv.tevkifatAmount || 0);
    const vatTotal = (inv.vatBreakdown || []).reduce((s, g) => s + Number(g.kdv || 0), 0);
    const alicidanKdv = vatTotal - tevkifatAmount;        // Alıcı tarafından ödenmeyecek KDV
    const tahsilEdilecek = Number(inv.total || 0) - tevkifatAmount; // Satıcının tahsil edeceği tutar

    const tevkifatHTML = isTevkifat ? `
<div class="tevkifat-box">
  <div class="tevkifat-title">KDV TEVKİFATI TABLOSU (SORUMLU SIFATIYLA)</div>
  <table class="tevkifat-table">
    <tr><td>KDV Matrahı (Ara Toplam)</td><td class="r">${fmtPrint(inv.subtotal, inv.currency)}</td></tr>
    <tr><td>Hesaplanan KDV (Toplam)</td><td class="r">${fmtPrint(vatTotal, inv.currency)}</td></tr>
    <tr class="tevkifat-row"><td><b>Tevkifat Oranı:</b> ${tevkifatRate} — KDV'nin %${Math.round((tevkifatRate.split("/").map(Number)[0] / tevkifatRate.split("/").map(Number)[1]) * 100)}'i kesilir</td><td class="r"><b>-${fmtPrint(tevkifatAmount, inv.currency)}</b></td></tr>
    <tr class="alici-row"><td>Alıcı Tarafından Ödenecek KDV</td><td class="r">${fmtPrint(alicidanKdv, inv.currency)}</td></tr>
    <tr class="tahsil-row"><td><b>Satıcıya Tahsil Edilecek Toplam</b></td><td class="r"><b>${fmtPrint(tahsilEdilecek, inv.currency)}</b></td></tr>
  </table>
  <div class="tevkifat-note">* Tevkifat tutarı (${fmtPrint(tevkifatAmount, inv.currency)}) alıcı tarafından vergi dairesine ayrıca beyan ve ödeme yapılacaktır.</div>
</div>` : "";

    const html = `
<!DOCTYPE html><html lang="tr"><head><meta charset="utf-8"><title>${typeLbl} ${inv.id}</title>
<style>@page{size:A4;margin:14mm 12mm}*{box-sizing:border-box}body{font-family:'Segoe UI',Roboto,Arial,sans-serif;color:#1a1a1a;margin:0;font-size:11.5px}
.header{display:flex;justify-content:space-between;padding-bottom:12px;border-bottom:2px solid #E5322A}.header img{height:46px}
.title h1{margin:16px 0 6px;font-size:22px;font-weight:800;letter-spacing:3px;color:#E5322A}
.meta{display:grid;grid-template-columns:1fr 1fr;gap:6px 24px;margin:8px 0 16px;padding:10px 14px;background:#F5F5F7;border:1px solid #E5E5EA;border-radius:6px;font-size:11.5px}
.meta b{color:#333;margin-right:8px}
table{width:100%;border-collapse:collapse;font-size:11px}th{background:#E5322A;color:#fff;padding:7px 6px;border:1px solid #B0221D;font-weight:700}td{border:1px solid #C8C8CE;padding:7px 6px}tr:nth-child(even) td{background:#FAFAFB}.c{text-align:center}.r{text-align:right}.b{font-weight:700}
.grand{background:#1E293B;color:#fff;padding:10px 14px;display:flex;justify-content:space-between;margin-top:8px;border-radius:4px;font-size:14px;font-weight:800;letter-spacing:1px}
.conv{margin-top:12px;padding:12px;background:#FFF7ED;border:1px dashed #FB923C;border-radius:6px;font-size:11px}
.conv .row{display:grid;grid-template-columns:repeat(3,1fr);gap:10px}.conv .row div{display:flex;justify-content:space-between;padding:6px 8px;background:#fff;border-radius:4px;border:1px solid #FED7AA}
.tevkifat-box{margin-top:12px;padding:12px 14px;background:#F5F0FF;border:1.5px solid #7C3AED;border-radius:6px}
.tevkifat-title{font-size:10px;font-weight:800;letter-spacing:1.5px;text-transform:uppercase;color:#5B21B6;margin-bottom:8px}
.tevkifat-table{border:none;font-size:11px}.tevkifat-table td{border:none;padding:4px 6px;background:transparent}
.tevkifat-row td{background:#EDE9FE;color:#5B21B6;font-weight:600;border-radius:3px}
.alici-row td{color:#059669}
.tahsil-row td{background:#1E293B;color:#fff;font-weight:800;font-size:12px;border-radius:3px;padding:6px 8px}
.tevkifat-note{font-size:9.5px;color:#7C3AED;margin-top:8px;font-style:italic}
</style></head><body>
<div class="header">
  <div>${logoUrl ? `<img src="${logoUrl}" alt="ZEN">` : `<h2 style="margin:0;color:#E5322A">ZEN</h2>`}</div>
  <div style="text-align:right;font-size:10.5px;line-height:1.5;color:#444"><b>ZEN Mekanizmalı Perde Sistemleri</b><br>info@zenperde.com · www.zenperde.com<br>TCMB: 1 EUR=₺${rates.eurToTl.toFixed(2)} · 1 USD=₺${rates.usdToTl.toFixed(2)}</div>
</div>
<div class="title"><h1>${typeLbl}${isTevkifat ? ' <span style="font-size:13px;background:#7C3AED;color:#fff;padding:2px 8px;border-radius:3px;vertical-align:middle;letter-spacing:1px">TEVKİFATLI</span>' : ''}</h1></div>
<div class="meta">
  <div><b>Belge No:</b> ${inv.id}</div>
  <div><b>Müşteri:</b> ${(inv.customerName || "-").replace(/</g, "&lt;")}</div>
  <div><b>Kesim No:</b> ${inv.docNo || "-"}</div>
  <div><b>Bağlı Sipariş:</b> ${inv.orderId || "-"}</div>
  <div><b>Tarih:</b> ${inv.issueDate}</div>
  <div><b>Vade:</b> ${inv.dueDate || "-"}</div>
</div>
<table><thead><tr><th style="width:5%">#</th><th style="width:40%">Ürün / Hizmet</th><th style="width:10%">Adet</th><th style="width:15%">B.Fiyat</th><th style="width:10%">KDV</th><th style="width:20%">Tutar</th></tr></thead>
<tbody>${rows}
<tr><td colspan="5" class="r">Ara Toplam:</td><td class="r b">${fmtPrint(inv.subtotal, inv.currency)}</td></tr>
${(inv.vatBreakdown || []).sort((a,b) => a.rate-b.rate).map(g => `<tr><td colspan="5" class="r">KDV %${g.rate}:</td><td class="r">${fmtPrint(g.kdv, inv.currency)}</td></tr>`).join("")}
</tbody></table>
<div class="grand"><span>GENEL TOPLAM${isTevkifat ? " (KDV Dahil)" : ""}</span><span>${fmtPrint(inv.total, inv.currency)}</span></div>
${tevkifatHTML}
<div class="conv"><div style="font-size:10px;text-transform:uppercase;letter-spacing:1.5px;color:#9A3412;font-weight:700;margin-bottom:6px">Diğer Para Birimlerinde Karşılık</div>
<div class="row"><div><span>Türk Lirası</span><b>${fmtPrint(totalConv.tl, "TL")}</b></div><div><span>Euro</span><b>${fmtPrint(totalConv.eur, "EUR")}</b></div><div><span>ABD Doları</span><b>${fmtPrint(totalConv.usd, "USD")}</b></div></div></div>
${inv.notes ? `<div style="margin-top:14px;padding:10px;background:#F5F5F7;border-radius:6px;font-size:10.5px"><b>Notlar:</b> ${(inv.notes || "").replace(/</g, "&lt;")}</div>` : ""}
</body></html>`;
    printViaIframe(html, false);
  };

  return (
    <div className="space-y-4 zen-anim-up">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-2xl font-extrabold tracking-tight flex items-center gap-2.5"><ModuleIcon tab="invoices" icon={Receipt} /> Fatura & İrsaliye</h2>
        <button
          onClick={() => setEditing({})}
          className="zen-btn-primary" data-testid="new-invoice-btn"
        ><Plus className="w-4 h-4" /> Yeni {TYPES.find(t => t.id === type)?.label}</button>
      </div>

      {/* Type tabs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {TYPES.map(t => {
          const I = t.icon; const active = type === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setType(t.id)}
              data-testid={`invoice-tab-${t.id}`}
              className={`p-3 rounded-lg border-2 transition-all flex items-center gap-3 ${
                active
                  ? `border-${t.color}-500 bg-${t.color}-500/10 text-${t.color}-600 dark:text-${t.color}-400`
                  : "border-slate-200 dark:border-[#22304A] hover:border-slate-300 text-slate-600 dark:text-slate-300"
              }`}
            >
              <I className="w-5 h-5" />
              <span className="text-sm font-semibold">{t.label}</span>
            </button>
          );
        })}
      </div>

      {/* Invoices list */}
      <div className="zen-panel p-3 overflow-x-auto">
        {loading ? (
          <div className="p-6 text-center text-sm text-slate-500">Yükleniyor…</div>
        ) : invoices.length === 0 ? (
          <div className="p-8 text-center">
            <FileText className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <div className="text-sm text-slate-500">Bu türde fatura yok. Yeni fatura oluşturmak için üstteki "Yeni" butonunu kullanın.</div>
          </div>
        ) : (
          <table className="zen-table">
            <thead><tr>
              <th>Belge No</th><th>Kesim No</th><th>Tarih</th><th>Müşteri</th>
              <th>Kategori</th><th className="text-right">Tutar</th><th>Durum</th><th></th>
            </tr></thead>
            <tbody>
              {invoices.slice((invPage - 1) * invPageSize, invPage * invPageSize).map(inv => {
                const CAT_MAP = {
                  nakit:        { label: "Nakit",        cls: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" },
                  resmi:        { label: "Resmi",         cls: "bg-sky-500/10 text-sky-600 border-sky-500/20" },
                  bilgi_fisi:   { label: "Bilgi Fişi",   cls: "bg-slate-500/10 text-slate-500 border-slate-500/20" },
                  iade:         { label: "İade",          cls: "bg-amber-500/10 text-amber-600 border-amber-500/20" },
                  tevkifatli:   { label: "Tevkifat",      cls: "bg-purple-500/10 text-purple-600 border-purple-500/20" },
                  ihracat:      { label: "İhracat",       cls: "bg-blue-500/10 text-blue-600 border-blue-500/20" },
                  ihrac_kayitli:{ label: "İhraç Kayıtlı", cls: "bg-indigo-500/10 text-indigo-600 border-indigo-500/20" },
                };
                const cat = CAT_MAP[inv.invoiceCategory] || CAT_MAP["nakit"];
                return (
                  <tr key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                    <td className="zen-mono font-bold text-[#E5322A]">{inv.id}</td>
                    <td>{inv.docNo || "-"}</td>
                    <td>{inv.issueDate}</td>
                    <td>{inv.customerName}</td>
                    <td><span className={`zen-chip ${cat.cls}`}>{cat.label}</span></td>
                    <td className="text-right zen-mono font-bold">{fmt(inv.total, inv.currency)}</td>
                    <td><span className={`zen-chip ${inv.status === "odendi" ? "bg-emerald-500/15 text-emerald-600 border-emerald-500/30" : "bg-amber-500/15 text-amber-600 border-amber-500/30"}`}>{inv.status || "kesildi"}</span></td>
                    <td className="text-right whitespace-nowrap">
                      <button onClick={() => printInvoice(inv)} className="p-1.5 hover:bg-slate-100 dark:hover:bg-[#22304A] rounded" title="Yazdır"><Printer className="w-4 h-4" /></button>
                      <button onClick={() => setEditing(inv)} className="p-1.5 hover:bg-slate-100 dark:hover:bg-[#22304A] rounded" title="Düzenle"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => del(inv.id)} className="p-1.5 hover:bg-rose-500/10 text-rose-500 rounded" title="Sil"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {invoices.length > 0 && (
          <ZenPagination total={invoices.length} page={invPage} pageSize={invPageSize} onPage={setInvPage} onPageSize={(s) => { setInvPageSize(s); setInvPage(1); }} />
        )}
      </div>

      {editing !== null && (
        <InvoiceEditor
          invoice={editing}
          defaultType={type}
          customers={customers}
          orders={orders}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function InvoiceEditor({ invoice, defaultType, customers, orders, onClose, onSaved }) {
  const isEdit = !!invoice?.id;
  const [f, setF] = useState(() => ({
    type: invoice?.type || defaultType,
    invoiceCategory: invoice?.invoiceCategory || "nakit",
    docNo: invoice?.docNo || "",
    customerId: invoice?.customerId || "",
    customerName: invoice?.customerName || "",
    orderId: invoice?.orderId || "",
    issueDate: invoice?.issueDate || new Date().toISOString().slice(0, 10),
    dueDate: invoice?.dueDate || "",
    currency: invoice?.currency || "TL",
    items: invoice?.items || [{ product: "", qty: 1, unitPrice: 0, kdvRate: 20, lineTotal: 0, currency: invoice?.currency || "TL" }],
    notes: invoice?.notes || "",
    status: invoice?.status || "kesildi",
    // Kategori bazlı ek alanlar
    relatedInvoiceNo: invoice?.relatedInvoiceNo || "",
    tevkifatRate: invoice?.tevkifatRate || "",
    tevkifatAmount: invoice?.tevkifatAmount || 0,
    exportCountry: invoice?.exportCountry || "",
    customsDeclarationNo: invoice?.customsDeclarationNo || "",
    eInvoiceType: invoice?.eInvoiceType || "e-Arşiv",
  }));

  const setItem = (i, k, v) => {
    const items = [...f.items];
    items[i] = { ...items[i], [k]: v };
    if (k === "qty" || k === "unitPrice") {
      items[i].lineTotal = Number(items[i].qty || 0) * Number(items[i].unitPrice || 0);
    }
    setF({ ...f, items });
  };
  const addItem = () => setF({ ...f, items: [...f.items, { product: "", qty: 1, unitPrice: 0, kdvRate: 20, lineTotal: 0, currency: f.currency }] });
  const rmItem = (i) => setF({ ...f, items: f.items.filter((_, x) => x !== i) });

  const linkOrder = (oid) => {
    const o = orders.find(x => x.id === oid);
    if (!o) return;
    setF({
      ...f,
      orderId: o.id,
      customerId: o.customerId,
      customerName: o.customerName,
      currency: o.currency,
      items: o.items.map(it => ({ product: it.product, qty: it.qty, unitPrice: it.unitPrice, kdvRate: it.kdvRate ?? 20, lineTotal: it.lineTotal, currency: it.currency || o.currency })),
    });
  };

  const subtotal = f.items.reduce((s, it) => s + Number(it.lineTotal || 0), 0);
  const vatBreakdown = {};
  f.items.forEach(it => {
    const r = Number(it.kdvRate ?? 20);
    vatBreakdown[r] = vatBreakdown[r] || { rate: r, net: 0, kdv: 0 };
    vatBreakdown[r].net += Number(it.lineTotal || 0);
    vatBreakdown[r].kdv += Number(it.lineTotal || 0) * r / 100;
  });
  const vatAmount = Object.values(vatBreakdown).reduce((s, g) => s + g.kdv, 0);
  const total = subtotal + vatAmount;

  // Tevkifat hesabı (örn. "2/10" → KDV'nin 2/10'u alıcı tarafından kesilir)
  const tevkifatCalc = (() => {
    if (f.invoiceCategory !== "tevkifatli" || !f.tevkifatRate) return 0;
    const parts = f.tevkifatRate.split("/").map(Number);
    if (parts.length === 2 && parts[1] > 0) return vatAmount * (parts[0] / parts[1]);
    return 0;
  })();
  const netCollectible = total - tevkifatCalc;

  const submit = async () => {
    if (!f.customerId) { toast.error("Cari seçin"); return; }
    if (f.items.length === 0 || !f.items.some(it => it.product)) { toast.error("En az bir ürün ekleyin"); return; }
    const payload = { ...f, subtotal, vatBreakdown: Object.values(vatBreakdown), vatAmount, total, tevkifatAmount: tevkifatCalc };
    try {
      if (isEdit) await api.put(`/invoices/${invoice.id}`, payload);
      else await api.post("/invoices", payload);
      toast.success(isEdit ? "Fatura güncellendi" : "Fatura oluşturuldu ve cari bakiye güncellendi");
      onSaved();
    } catch (e) { toast.error("Kaydedilemedi"); }
  };

  const cust = customers.find(c => c.id === f.customerId);
  useEffect(() => { if (cust && !isEdit) setF(v => ({ ...v, customerName: cust.name, currency: cust.currency })); }, [f.customerId]);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="zen-panel w-full max-w-4xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-200 dark:border-[#22304A] flex justify-between items-center sticky top-0 bg-white dark:bg-[#131B2E] z-10">
          <h3 className="text-lg font-bold">{isEdit ? `Fatura Düzenle - ${invoice.id}` : "Yeni Fatura"}</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          {/* Meta */}
          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1">Fatura Türü</label>
              <select className="zen-input" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>
                <option value="satis">Satış Faturası</option>
                <option value="alis">Alış Faturası</option>
                <option value="satis_iade">Satış İadesi</option>
                <option value="alis_iade">Alış İadesi</option>
                <option value="irsaliye">İrsaliye</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">Fatura Kategorisi</label>
              <select className="zen-input" value={f.invoiceCategory} onChange={(e) => setF({ ...f, invoiceCategory: e.target.value })} data-testid="inv-category">
                <option value="nakit">Nakit Tahsilat</option>
                <option value="resmi">Resmi Fatura (e-Fatura / e-Arşiv)</option>
                <option value="bilgi_fisi">Bilgi Fişi</option>
                <option value="iade">İade Faturası</option>
                <option value="tevkifatli">Tevkifatlı Fatura</option>
                <option value="ihracat">İhracat Faturası</option>
                <option value="ihrac_kayitli">İhraç Kayıtlı Satış</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">Cari</label>
              <select data-testid="inv-customer" className="zen-input" value={f.customerId} onChange={(e) => setF({ ...f, customerId: e.target.value })}>
                <option value="">-- seçin --</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.name} ({c.currency})</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">Bağlı Sipariş (opsiyonel)</label>
              <select className="zen-input" value={f.orderId} onChange={(e) => linkOrder(e.target.value)}>
                <option value="">-- yok --</option>
                {orders.filter(o => !f.customerId || o.customerId === f.customerId).slice(0, 100).map(o => (
                  <option key={o.id} value={o.id}>{o.id} · {fmt(o.total, o.currency)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">Kesim No / Seri</label>
              <input className="zen-input" placeholder="Örn: A-2026-00456" value={f.docNo} onChange={(e) => setF({ ...f, docNo: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">Tarih</label>
              <input type="date" className="zen-input" value={f.issueDate} onChange={(e) => setF({ ...f, issueDate: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">Vade Tarihi</label>
              <input type="date" className="zen-input" value={f.dueDate} onChange={(e) => setF({ ...f, dueDate: e.target.value })} />
            </div>
            {f.invoiceCategory === "resmi" && (
              <div>
                <label className="block text-xs font-semibold mb-1">e-Fatura Türü</label>
                <select className="zen-input" value={f.eInvoiceType} onChange={(e) => setF({ ...f, eInvoiceType: e.target.value })}>
                  <option value="e-Arşiv">e-Arşiv</option>
                  <option value="e-Fatura">e-Fatura</option>
                </select>
              </div>
            )}
          </div>

          {/* Kategori bazlı ek alanlar */}
          {f.invoiceCategory === "iade" && (
            <div className="zen-panel p-4 space-y-2 border-l-4 border-amber-400">
              <div className="text-xs font-bold text-amber-600 dark:text-amber-400 mb-2 flex items-center gap-1.5">
                <RefreshCw className="w-3.5 h-3.5" /> İade Faturası Detayları
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1">İade Edilen Orijinal Fatura No</label>
                <input className="zen-input" placeholder="Örn: INV-2026-A1B2C" value={f.relatedInvoiceNo}
                  onChange={(e) => setF({ ...f, relatedInvoiceNo: e.target.value })} data-testid="inv-related-no" />
              </div>
            </div>
          )}

          {f.invoiceCategory === "tevkifatli" && (
            <div className="zen-panel p-4 space-y-3 border-l-4 border-purple-400">
              <div className="text-xs font-bold text-purple-600 dark:text-purple-400 mb-2 flex items-center gap-1.5">
                <Receipt className="w-3.5 h-3.5" /> Tevkifat Detayları
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold mb-1">Tevkifat Oranı</label>
                  <select className="zen-input" value={f.tevkifatRate} onChange={(e) => setF({ ...f, tevkifatRate: e.target.value })} data-testid="inv-tevkifat-rate">
                    <option value="">-- seçin --</option>
                    <option value="2/10">2/10 — %20 tevkifat (yapı inş. vb.)</option>
                    <option value="3/10">3/10 — %30 tevkifat</option>
                    <option value="4/10">4/10 — %40 tevkifat (temizlik, bahçe vb.)</option>
                    <option value="5/10">5/10 — %50 tevkifat (iş gücü temin vb.)</option>
                    <option value="7/10">7/10 — %70 tevkifat</option>
                    <option value="9/10">9/10 — %90 tevkifat (yük taşıma vb.)</option>
                  </select>
                </div>
                <div className="flex items-end">
                  {tevkifatCalc > 0 && (
                    <div className="zen-panel p-3 w-full text-xs space-y-1">
                      <div className="flex justify-between"><span className="text-slate-500">KDV Toplamı</span><span className="zen-mono">{fmt(vatAmount, f.currency)}</span></div>
                      <div className="flex justify-between text-purple-600"><span className="font-semibold">Tevkifat Tutarı</span><span className="zen-mono font-bold">-{fmt(tevkifatCalc, f.currency)}</span></div>
                      <div className="flex justify-between text-emerald-600 border-t border-slate-200 dark:border-[#22304A] pt-1 font-bold"><span>Tahsil Edilecek</span><span className="zen-mono">{fmt(netCollectible, f.currency)}</span></div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {(f.invoiceCategory === "ihracat" || f.invoiceCategory === "ihrac_kayitli") && (
            <div className="zen-panel p-4 space-y-3 border-l-4 border-sky-400">
              <div className="text-xs font-bold text-sky-600 dark:text-sky-400 mb-2 flex items-center gap-1.5">
                <Truck className="w-3.5 h-3.5" />
                {f.invoiceCategory === "ihracat" ? "İhracat Faturası Detayları" : "İhraç Kayıtlı Satış Detayları"}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold mb-1">Hedef Ülke</label>
                  <input className="zen-input" placeholder="Örn: Almanya, Fransa" value={f.exportCountry}
                    onChange={(e) => setF({ ...f, exportCountry: e.target.value })} data-testid="inv-export-country" />
                </div>
                {f.invoiceCategory === "ihracat" && (
                  <div>
                    <label className="block text-xs font-semibold mb-1">Gümrük Beyanname No</label>
                    <input className="zen-input" placeholder="Örn: 26350100EX202600001" value={f.customsDeclarationNo}
                      onChange={(e) => setF({ ...f, customsDeclarationNo: e.target.value })} data-testid="inv-customs-no" />
                  </div>
                )}
              </div>
              {f.invoiceCategory === "ihrac_kayitli" && (
                <div className="p-3 rounded-lg bg-sky-500/5 border border-sky-500/20 text-[11px] text-sky-700 dark:text-sky-300">
                  İhraç kayıtlı satışlarda KDV hesaplanır ancak alıcıdan tahsil edilmez. KDV iade talebinde gümrük çıkış beyannamesi gereklidir.
                </div>
              )}
            </div>
          )}

          {/* Items */}
          <div className="zen-panel !p-0 overflow-x-auto">
            <table className="zen-table !text-xs">
              <thead><tr><th>Ürün / Hizmet</th><th className="w-16">Adet</th><th className="w-24">B.Fiyat</th><th className="w-16">KDV %</th><th className="w-24 text-right">Tutar</th><th className="w-10"></th></tr></thead>
              <tbody>
                {f.items.map((it, i) => (
                  <tr key={i}>
                    <td><input className="zen-input !py-1 !text-xs" value={it.product} onChange={(e) => setItem(i, "product", e.target.value)} /></td>
                    <td><input type="number" className="zen-input !py-1 !text-xs !text-center" value={it.qty} onChange={(e) => setItem(i, "qty", Number(e.target.value))} /></td>
                    <td><input type="number" step="0.01" className="zen-input !py-1 !text-xs !text-right" value={it.unitPrice} onChange={(e) => setItem(i, "unitPrice", Number(e.target.value))} /></td>
                    <td><input type="number" className="zen-input !py-1 !text-xs !text-center" value={it.kdvRate} onChange={(e) => setItem(i, "kdvRate", Number(e.target.value))} /></td>
                    <td className="text-right zen-mono font-bold">{fmt(it.lineTotal, f.currency)}</td>
                    <td><button onClick={() => rmItem(i)} className="p-1 text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button onClick={addItem} className="zen-btn-ghost !text-xs m-3"><Plus className="w-3.5 h-3.5" /> Satır Ekle</button>
          </div>

          {/* Totals */}
          <div className="flex justify-end">
            <div className="w-full md:w-96 zen-panel p-4 text-sm space-y-1">
              <div className="flex justify-between"><span>Ara Toplam</span><span className="zen-mono">{fmt(subtotal, f.currency)}</span></div>
              {Object.values(vatBreakdown).sort((a, b) => a.rate - b.rate).map(g => (
                <div key={g.rate} className="flex justify-between text-xs text-slate-500"><span>KDV %{g.rate}</span><span className="zen-mono">{fmt(g.kdv, f.currency)}</span></div>
              ))}
              <div className="border-t border-slate-200 dark:border-[#22304A] pt-2 mt-2 flex justify-between text-base font-extrabold text-[#E5322A]"><span>TOPLAM</span><span className="zen-mono">{fmt(total, f.currency)}</span></div>
            </div>
          </div>

          <textarea className="zen-input" rows={2} placeholder="Notlar..." value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
        </div>
        <div className="p-5 border-t border-slate-200 dark:border-[#22304A] flex justify-end gap-2 sticky bottom-0 bg-white dark:bg-[#131B2E]">
          <button onClick={onClose} className="zen-btn-ghost">İptal</button>
          <button onClick={submit} className="zen-btn-primary" data-testid="inv-save"><CheckCircle2 className="w-4 h-4" /> Kaydet & Cari İşle</button>
        </div>
      </div>
    </div>
  );
}

// ============ AI CHAT FLOATING (Admin only) ============

export { ProductGroupsModule, PGForm, MatricesModule, SparePartsModule, SparePartForm, StockManagementModule, PriceListsModule, PackingListModule, PackingListDialog, ProductWeightsModule, InvoicesModule, InvoiceEditor };
export default PackingListModule;
